import React, { useState, createContext, useContext, useEffect, useMemo } from "react";
import moment from "moment";
import "./../../App.css";
import Select, { components } from "react-select";
import { customStyles } from "./../../helpers/StyleUtils";
import late from "./../../images/late.svg";
import early from "./../../images/early.svg";
import notesicon from "./../../images/notes.svg";
import plus from "./../../images/add.svg";
import shiftsAvailability from "./../../images/availability.svg";
import BookingModal from './BookingModal';
import Modal from "react-modal";
import { FixedSizeList as List } from 'react-window';
import { ClipLoader } from "react-spinners";
import { talentService, bookingService, noteService, talentScheduleService, locationService, companyService, talentGroupService } from '../../api/apiService';

// Set app element for Modal
Modal.setAppElement('#root');

// Calendar Context
const CalendarContext = createContext();

const CalendarProvider = ({ children }) => {
  // Calculate the start of the week (Monday)
  const startOfWeek = moment.utc().add(2, "hours").startOf("isoWeek");

  const [dateRange, setDateRange] = useState({
    start: startOfWeek.toDate(),
    end: moment(startOfWeek).add(6, "days").toDate(), // Add 6 days to get to Sunday
  });

  const updateDateRange = (newRange) => {
    setDateRange(newRange);
  };
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [scheduleDataMap, setScheduleDataMap] = useState({});

  const clearSelectedClient = () => setSelectedClient(null);

  return (
    <CalendarContext.Provider
      value={{
        dateRange,
        updateDateRange,
        selectedClient,
        setSelectedClient,
        clearSelectedClient,
        selectedStatus,
        setSelectedStatus,
        scheduleDataMap,
        setScheduleDataMap,
      }}
    >
      {children}
    </CalendarContext.Provider>
  );
};

const useCalendar = () => useContext(CalendarContext);

const DateNavigator = () => {
  const { dateRange, updateDateRange, clearSelectedClient } = useCalendar();

  const navigate = (days) => {
    let newStart = moment(dateRange.start).add(days, "days");
    let newEnd = moment(newStart).add(6, "days");

    updateDateRange({ start: newStart.toDate(), end: newEnd.toDate() });
    clearSelectedClient();
  };

  const generateWeekGrid = () => {
    let grid = [];
    let currentDate = new Date(dateRange.start);

    for (let i = 0; i < 7; i++) {
      const date = new Date(currentDate);
      grid.push(date);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return grid;
  };

  const weekGrid = generateWeekGrid();

  return (
    <div
      style={{
        position: "sticky",
        top: "5px",
        padding: "20px 0",
        backgroundColor: "#ffffff",
        zIndex: "4",
      }}
    >
      <div className="tlistcal-week-grid">
        <div style={{ flexBasis: "200px", display: "flex", gap: "5px" }}>
          <button 
            className="button" 
            onClick={() => navigate(-7)}
            style={{
              padding: "5px 12px",
              fontSize: "12px",
              minWidth: "50px"
            }}
          >
            Prev
          </button>
          <button 
            className="button" 
            onClick={() => navigate(7)}
            style={{
              padding: "5px 12px",
              fontSize: "12px",
              minWidth: "50px"
            }}
          >
            Next
          </button>
        </div>
        <div style={{
          width: "70px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontWeight: "600",
          fontSize: "16px"
        }}>
          {moment(dateRange.start).format('YYYY')}
        </div>

        {weekGrid.map((day, index) => (
          <div key={index} style={{textAlign: "center"}} className="tlistcal-day-column">
            {moment(day).format('dddd DD.MM')}
          </div>
        ))}
      </div>
    </div>
  );
};

// Talent Component
const Talent = ({ talent }) => {
  const { dateRange, scheduleDataMap, setScheduleDataMap } = useCalendar();
  const [showDetail, setshowDetail] = useState(false);
  const [tooltipStates, setTooltipStates] = useState({});
  const [availabilities, setAvailabilities] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalNotesOpen, setIsModalNotesOpen] = useState(false);
  const [isModalBookingOpen, setIsModalBookingOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [notesForDay, setNotesForDay] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [newAvailability, setNewAvailability] = useState({ startDate: '', endDate: '' });
  const [availabilityStatus, setAvailabilityStatus] = useState('available');
  const [availabilityShift, setAvailabilityShift] = useState('full-day'); // 'full-day', 'early', 'late'
  const [isMultipleDays, setIsMultipleDays] = useState(false);
  const [showToDateCalendar, setShowToDateCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTalentId, setSelectedTalentId] = useState('');
  const [selectedTalentPosition, setSelectedTalentPosition] = useState('');
  const [selectedTalentSet, setSelectedTalentSet] = useState('');
  const [selectedTalentEmail, setSelectedTalentEmail] = useState('');
  const [selectedTalentName, setSelectedTalentName] = useState('');
  const [selectedTalentRate, setSelectedTalentRate] = useState('');
  const [notes, setNotes] = useState([]);
  const [awaitings, setAwaitings] = useState([]);
  const [secondoptions, setSecondoptions] = useState([]);
  const [isNotizModalOpen, setIsNotizModalOpen] = useState(false);
  const [notizSetcast, setNotizSetcast] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedNotiz, setEditedNotiz] = useState('');
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  
  // Product Lead Notes state (read-only)
  const [isLeadNotesModalOpen, setIsLeadNotesModalOpen] = useState(false);
  const [notizLead, setNotizLead] = useState('');
  const [isLoadingLeadNotes, setIsLoadingLeadNotes] = useState(false);

  // Transform the talent object to match the expected format if needed
  const formattedTalent = {
    id: talent.id,
    Name: talent.name || '',
    Funktion: talent.funktion || talent.function || '',
    Bereich: talent.bereich || talent.set || talent.area || '',
    E_Mail: talent.email || talent.E_Mail || '',
    Telefon: talent.phone || talent.Telefon || '',
    Tagessatz: talent.rate || talent.Tagessatz || '',
    Notiz_Setcast: talent.notes || talent.Notiz_Setcast || ''
  };

  // Refresh schedule for this talent from the API (called after saves)
  const refreshSchedule = async () => {
    if (!talent.id) return;
    const weekStart = moment(dateRange.start).format('YYYY-MM-DD');
    const weekEnd = moment(dateRange.end).format('YYYY-MM-DD');
    try {
      const response = await talentScheduleService.getTalentScheduleByRange(talent.id, weekStart, weekEnd);
      const schedule = response?.data?.schedule || {};
      setScheduleDataMap(prev => ({
        ...prev,
        [talent.id]: { talent_id: talent.id, schedule }
      }));
    } catch (err) {
      console.error('Error refreshing schedule for talent:', talent.id, err);
    }
  };

  // Read schedule data from context (populated by batch fetch in parent)
  useEffect(() => {
    const data = scheduleDataMap[talent.id];
    if (!data) return;
    const schedule = data.schedule || {};

    const availabilitiesData = Array.isArray(schedule.availabilities) ? schedule.availabilities : [];
    const vacationsData = Array.isArray(schedule.vacations) ? schedule.vacations : [];
    const awaitingsData = Array.isArray(schedule.awaitings) ? schedule.awaitings : [];
    const secondoptionsData = Array.isArray(schedule.secondoptions) ? schedule.secondoptions : [];

    setAvailabilities(availabilitiesData.map(item => ({
      startDate: new Date(item.start_date),
      endDate: new Date(item.end_date),
      addedAt: new Date(item.added_at || item.created_at),
      shift: item.shift || 'full-day'
    })));
    setVacations(vacationsData.map(item => ({
      startDate: new Date(item.start_date),
      endDate: new Date(item.end_date),
      addedAt: new Date(item.added_at || item.created_at)
    })));
    setAwaitings(awaitingsData.map(item => ({
      startDate: new Date(item.start_date),
      endDate: new Date(item.end_date),
      addedAt: new Date(item.added_at || item.created_at)
    })));
    setSecondoptions(secondoptionsData.map(item => ({
      startDate: new Date(item.start_date),
      endDate: new Date(item.end_date),
      addedAt: new Date(item.added_at || item.created_at)
    })));
    setNotes(schedule.notes || []);

    const filteredBookings = (schedule.bookings || []).filter(b => b.status !== 'deleted' && b.status !== 'canceled');
    setBookings(filteredBookings);
  }, [scheduleDataMap, talent.id]);

  // Fallback: individual fetch (only used if talent wasn't in the batch)
  useEffect(() => {
    const fetchAvailabilityAndVacation = async () => {
      try {
        if (!talent.id) {
          console.error('No talent ID available for fetching availability');
          setAvailabilities([]);
          setVacations([]);
          setAwaitings([]);
          setNotes([]);
          return;
        }

        // Skip if batch data already loaded
        if (scheduleDataMap[talent.id]) return;

        const weekStart = moment(dateRange.start).format('YYYY-MM-DD');
        const weekEnd = moment(dateRange.end).format('YYYY-MM-DD');

        const response = await talentScheduleService.getTalentScheduleByRange(talent.id, weekStart, weekEnd);
        // console.log('Received range response:', response);
        
        const data = response.data || {};
        const schedule = data.schedule || {};
        
        const scheduledNotes = schedule.notes || [];
        setNotes(scheduledNotes);
        
        // Process availabilities, vacations, awaitings, secondoptions - INCLUDING RELATED TALENTS
        // The API already returns combined data from all related talents, so don't filter by talent_id
        const availabilitiesData = Array.isArray(schedule.availabilities) ? schedule.availabilities : [];
        const vacationsData = Array.isArray(schedule.vacations) ? schedule.vacations : [];
        const awaitingsData = Array.isArray(schedule.awaitings) ? schedule.awaitings : [];
        const secondoptionsData = Array.isArray(schedule.secondoptions) ? schedule.secondoptions : [];
        
        // console.log('Related talent IDs for', talent.Name, ':', schedule.related_talent_ids || []);
        // console.log('Combined schedule data from all related talents:', {
          // availabilities: availabilitiesData.length,
          // vacations: vacationsData.length,
          // awaitings: awaitingsData.length,
          // secondoptions: secondoptionsData.length
        // });
        
        // console.log('Combined entries from all related talents:', {
          // availabilities: availabilitiesData.length,
          // vacations: vacationsData.length,
          // awaitings: awaitingsData.length,
          // secondoptions: secondoptionsData.length
        // });

        // console.log('Raw availabilities:', availabilitiesData);
        // console.log('Raw vacations:', vacationsData);
        // console.log('Raw awaitings:', awaitingsData);
        // console.log('Raw secondoptions:', secondoptionsData);
        
        // Convert to our state format
        const availabilityEntries = availabilitiesData.map(item => ({
          startDate: new Date(item.start_date),
          endDate: new Date(item.end_date),
          addedAt: new Date(item.added_at || item.created_at),
          shift: item.shift || 'full-day' // Default to full-day if not specified
        }));
        
        const vacationEntries = vacationsData.map(item => ({
          startDate: new Date(item.start_date),
          endDate: new Date(item.end_date),
          addedAt: new Date(item.added_at || item.created_at),
          shift: item.shift || 'full-day'
        }));
        
        const awaitingEntries = awaitingsData.map(item => ({
          startDate: new Date(item.start_date),
          endDate: new Date(item.end_date),
          addedAt: new Date(item.added_at || item.created_at),
          shift: item.shift || 'full-day'
        }));
        
        const secondoptionEntries = secondoptionsData.map(item => ({
          startDate: new Date(item.start_date),
          endDate: new Date(item.end_date),
          addedAt: new Date(item.added_at || item.created_at),
          shift: item.shift || 'full-day'
        }));
        
        // console.log('Converted availability data for state:', {
          // availabilities: availabilityEntries,
          // vacations: vacationEntries,
          // awaitings: awaitingEntries,
          // secondoptions: secondoptionEntries
        // });
        
        // Update state with the final data
        setAvailabilities(availabilityEntries);
        setVacations(vacationEntries);
        setAwaitings(awaitingEntries);
        setSecondoptions(secondoptionEntries);
      } catch (error) {
        console.error("Unexpected error fetching availability:", error);
      }
    };

    fetchAvailabilityAndVacation();
  }, [talent.id, dateRange.start, dateRange.end, scheduleDataMap]);

  useEffect(() => {
    const fetchNotesForWeek = async () => {
      try {
        const formattedStart = moment(dateRange.start).format('YYYY-MM-DD');
        const formattedEnd = moment(dateRange.end).format('YYYY-MM-DD');
        const response = await noteService.getTalentNotesByDateRange(talent.id, formattedStart, formattedEnd);
        
        // Backend returns { success, data: [...], groupedByDate, pagination }
        const notesData = response.data?.data || [];
        setNotes(notesData);
      } catch (error) {
        console.error("Error fetching notes:", error);
      }
    };

    fetchNotesForWeek();
  }, [talent.id, dateRange.start, dateRange.end]);

  const saveAvailability = async () => {
    try {
      // Format dates correctly and validate them
      const startDate = moment(newAvailability.startDate).format('YYYY-MM-DD');
      let endDate = startDate; // Default to same day
      
      // Only use endDate if it's valid and multiple days is checked
      if (isMultipleDays && newAvailability.endDate && newAvailability.endDate.trim()) {
        const endMoment = moment(newAvailability.endDate);
        if (endMoment.isValid()) {
          endDate = endMoment.format('YYYY-MM-DD');
        } else {
          // console.warn('Invalid end date provided, using start date as end date');
          endDate = startDate;
        }
      }

      // console.log(`Adding ${availabilityStatus} schedule from ${startDate} to ${endDate}`, { 
        // isMultipleDays, 
        // originalEndDate: newAvailability.endDate 
      // });

      // If multiple days is checked and we have a valid end date different from start date
      if (isMultipleDays && endDate !== startDate) {
        const startMoment = moment(startDate);
        const endMoment = moment(endDate);
        
        // Ensure end date is after start date
        if (endMoment.isBefore(startMoment)) {
          alert('End date must be after start date');
          return;
        }
        
        const daysDifference = endMoment.diff(startMoment, 'days');
        
        // console.log(`Making ${daysDifference + 1} individual API calls for each day`);
        
        // Validate daysDifference
        if (isNaN(daysDifference) || daysDifference < 0) {
          console.error('Invalid days difference:', daysDifference);
          alert('Invalid date range selected');
          return;
        }
        
        // Create array of promises for each day
        const apiCalls = [];
        const newEntries = [];
        
        for (let i = 0; i <= daysDifference; i++) {
          const currentDate = moment(startDate).add(i, 'days').format('YYYY-MM-DD');
          
          const scheduleData = {
            talent_id: talent.id,
            start_date: currentDate,
            end_date: currentDate, // Each call is for a single day
            shift: availabilityShift // Include shift information
          };
          
          // Create the API call promise based on the selected status
          let apiCall;
          switch (availabilityStatus) {
            case 'available':
              apiCall = talentScheduleService.addAvailability(scheduleData);
              break;
            case 'not-available':
              apiCall = talentScheduleService.addVacation(scheduleData);
              break;
            case 'awaiting':
              apiCall = talentScheduleService.addAwaiting(scheduleData);
              break;
            case 'secondoption':
              apiCall = talentScheduleService.addSecondoption(scheduleData);
              break;
            default:
              throw new Error(`Unknown availability status: ${availabilityStatus}`);
          }
          
          apiCalls.push(apiCall);
          
          // Create entry for local state
          newEntries.push({
            startDate: new Date(currentDate),
            endDate: new Date(currentDate),
            addedAt: new Date(),
            shift: availabilityShift
          });
        }
        
        // Execute all API calls
        await Promise.all(apiCalls);
        // console.log(`Successfully created ${apiCalls.length} individual day entries`);
        
        // Update local state with all new entries
        switch (availabilityStatus) {
          case 'available':
            setAvailabilities(prev => [...prev, ...newEntries]);
            break;
          case 'not-available':
            setVacations(prev => [...prev, ...newEntries]);
            break;
          case 'awaiting':
            setAwaitings(prev => [...prev, ...newEntries]);
            break;
          case 'secondoption':
            setSecondoptions(prev => [...prev, ...newEntries]);
            break;
        }
        
      } else {
        // Single day or range call (original behavior)
        const scheduleData = {
          talent_id: talent.id,
          start_date: startDate,
          end_date: endDate,
          shift: availabilityShift // Include shift information
        };

        // console.log(`Adding ${availabilityStatus} schedule with data:`, scheduleData);
        
        // Call the appropriate API based on the selected status
        let response;
        switch (availabilityStatus) {
          case 'available':
            response = await talentScheduleService.addAvailability(scheduleData);
            break;
          case 'not-available':
            response = await talentScheduleService.addVacation(scheduleData);
            break;
          case 'awaiting':
            response = await talentScheduleService.addAwaiting(scheduleData);
            break;
          case 'secondoption':
            response = await talentScheduleService.addSecondoption(scheduleData);
            break;
          default:
            throw new Error(`Unknown availability status: ${availabilityStatus}`);
        }
        
        // Update local state
        const newEntry = {
          startDate: new Date(startDate),
          endDate: new Date(endDate),
          addedAt: new Date(),
          shift: availabilityShift
        };

        switch (availabilityStatus) {
          case 'available':
            setAvailabilities(prev => [...prev, newEntry]);
            break;
          case 'not-available':
            setVacations(prev => [...prev, newEntry]);
            break;
          case 'awaiting':
            setAwaitings(prev => [...prev, newEntry]);
            break;
          case 'secondoption':
            setSecondoptions(prev => [...prev, newEntry]);
            break;
        }
      }

      // Refresh scheduleDataMap so re-mounted rows (after scrolling) get fresh data
      await refreshSchedule();

      closeModal();
    } catch (error) {
      console.error("Error saving availability:", error);
      alert("Error saving availability: " + (error.response?.data?.message || error.message));
    }
  };

  const saveNote = async () => {
    const formattedDate = moment(selectedDay).format('YYYY-MM-DD');
    
    try {
      const noteData = {
        talent_id: talent.id,
        date: formattedDate,
        content: newNote
      };

      // Create a permanent note for immediate UI update
      const permanentNote = {
        id: Date.now(), // Use timestamp as ID
        talent_id: talent.id,
        date: formattedDate,
        content: newNote,
        notes: newNote, // alternative field name for compatibility
        created_at: new Date().toISOString(),
        user: localStorage.getItem("userEmail") || 'User'
      };

      // Update state immediately for instant visual feedback
      setNotes(prev => {
        // console.log('Adding permanent note to state for date:', formattedDate);
        const updatedNotes = [...prev, permanentNote];
        // console.log('Updated notes count:', updatedNotes.length);
        return updatedNotes;
      });
      setNotesForDay(prev => [...prev, permanentNote]);

      // Show success message immediately after state update
      alert("Note added successfully!");
      
      // Clear the input and close modal
      setNewNote("");
      closeNotesModal();

      // Send to API in background (don't replace the note, just save to server)
      await noteService.createNote(noteData);
      // console.log('Note saved to server successfully');
      
    } catch (error) {
      console.error("Error saving note:", error);
      alert("Error saving note. Please try again.");
    }
  };

  const openNotizModal = async () => {
    setIsLoadingNotes(true);
    try {
      const response = await talentService.getTalentById(talent.id);
      const currentNotiz = response.data?.talent?.Notiz_Setcast || talent.Notiz_Setcast || '';
      setNotizSetcast(currentNotiz);
      setEditedNotiz('');
      setIsNotizModalOpen(true);
    } catch (error) {
      console.error("Error fetching Notiz_Setcast:", error);
      setNotizSetcast(talent.Notiz_Setcast || '');
    } finally {
      setIsLoadingNotes(false);
    }
  };

  const saveNotizChanges = async () => {
    try {
      const updatedNotiz = `${editedNotiz}\n\n[Edited by ${localStorage.getItem("userEmail")} on ${new Date().toISOString()}]`;
      await talentService.updateTalent(talent.id, { Notiz_Setcast: updatedNotiz });
      setNotizSetcast(updatedNotiz);
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving notes:", error);
    }
  };

  const addNewNote = async () => {
    try {
      const userEmail = localStorage.getItem("userEmail");
      const timestamp = new Date().toISOString();
      
      // Create the new note text with the existing notes
      const existingNotes = talent.Notiz_Setcast || '';
      const newNoteText = `${existingNotes}\n\n${editedNotiz}\n[Added by ${userEmail} on ${timestamp}]`;

      // Update the talent data on the server
      await talentService.updateTalent(talent.id, { 
        Notiz_Setcast: newNoteText 
      });

      // Update local state
      setNotizSetcast(newNoteText);
      
      // Update the talent object directly
      talent.Notiz_Setcast = newNoteText;
      
      // Clear the input
      setEditedNotiz('');
      
      // Show success feedback (optional)
      alert("Note added successfully");
    } catch (error) {
      console.error("Error adding new note:", error);
      alert("Error adding note. Please try again.");
    }
  };

  // Lead Notes Functions - Show Product Lead Notes (same as TalentListApi)
  const openLeadNotesModal = async () => {
    setIsLoadingLeadNotes(true);
    try {
      const response = await talentService.getTalentById(talent.id);
      const talentData = response.data?.talent || response.data || talent;
      
      // Get Product Lead Notes (same fields as TalentListApi)
      const productLeadNotes = talentData.productLeadNotes || talentData.Product_Lead_Notes || talentData.notesProductLeads || '';
      const productLeadNotesType = talentData.productLeadNotesType || talentData.Product_Lead_Notes_Type || talentData.notesProductLeadsdd || null;
      
      // Format the display text
      let displayText = productLeadNotes;
      if (productLeadNotesType) {
        const typeLabel = productLeadNotesType.label || productLeadNotesType;
        displayText += productLeadNotes ? ` - ${typeLabel}` : typeLabel;
      }
      
      setNotizLead(displayText);
      setIsLeadNotesModalOpen(true);
    } catch (error) {
      console.error("Error fetching Product Lead Notes:", error);
      // Fallback to talent object data
      const productLeadNotes = talent.productLeadNotes || talent.Product_Lead_Notes || talent.notesProductLeads || '';
      const productLeadNotesType = talent.productLeadNotesType || talent.Product_Lead_Notes_Type || talent.notesProductLeadsdd || null;
      
      let displayText = productLeadNotes;
      if (productLeadNotesType) {
        const typeLabel = productLeadNotesType.label || productLeadNotesType;
        displayText += productLeadNotes ? ` - ${typeLabel}` : typeLabel;
      }
      
      setNotizLead(displayText || 'No product lead notes available');
    } finally {
      setIsLoadingLeadNotes(false);
    }
  };

  const closeLeadNotesModal = () => {
    setIsLeadNotesModalOpen(false);
  };

  // Function to check if a date is within any of the ranges
  const isDateWithinRange = (date, ranges) => {
    const formattedDate = moment(date).format('YYYY-MM-DD');
    // console.log(`Checking if ${formattedDate} is within any of ${ranges.length} ranges`);
    
    if (!Array.isArray(ranges) || ranges.length === 0) {
      // console.log(`No ranges to check against for ${formattedDate}`);
      return false;
    }

    // Find matching ranges
    const matchingRanges = ranges.filter(range => {
      const rangeStartDate = moment(range.startDate).format('YYYY-MM-DD');
      const rangeEndDate = moment(range.endDate).format('YYYY-MM-DD');
      const checkDate = formattedDate;
      
      // Simple string comparison for dates in YYYY-MM-DD format
      const isInRange = (checkDate === rangeStartDate) || 
                        (checkDate === rangeEndDate) || 
                        (checkDate > rangeStartDate && checkDate < rangeEndDate);
                        
      // console.log(`Range check: ${rangeStartDate} to ${rangeEndDate} for ${formattedDate} - matched: ${isInRange}`);
      
      return isInRange;
    });
    
    // console.log(`Found ${matchingRanges.length} matching ranges for ${formattedDate}`);
    return matchingRanges.length > 0;
  };

  // Function to get the latest entry for a specific date across all entry types
  const getLatestEntryForDate = (date, availEntries, vacationEntries, awaitingEntries, secondoptionEntries) => {
    const formattedDate = moment(date).format('YYYY-MM-DD');
    // console.log(`Finding latest entry for ${formattedDate}`);
    
    const isAvailable = isDateWithinRange(date, availEntries);
    const isVacation = isDateWithinRange(date, vacationEntries);
    const isAwaiting = isDateWithinRange(date, awaitingEntries);
    const isSecondoption = isDateWithinRange(date, secondoptionEntries);
    
    // console.log(`Date status: Available=${isAvailable}, Vacation=${isVacation}, Awaiting=${isAwaiting}, Secondoption=${isSecondoption}`);
    
    // Find all matching entries
    const matchingEntries = [];
    
    if (isAvailable) {
      const availMatches = availEntries.filter(entry => {
        const entryStartDate = moment(entry.startDate).format('YYYY-MM-DD');
        const entryEndDate = moment(entry.endDate).format('YYYY-MM-DD');
        
        return formattedDate === entryStartDate || 
               formattedDate === entryEndDate || 
               (formattedDate > entryStartDate && formattedDate < entryEndDate);
      });
      
      // console.log(`Found ${availMatches.length} available entries for ${formattedDate}`);
      availMatches.forEach(entry => matchingEntries.push({ type: 'available', entry }));
    }
    
    if (isVacation) {
      const vacationMatches = vacationEntries.filter(entry => {
        const entryStartDate = moment(entry.startDate).format('YYYY-MM-DD');
        const entryEndDate = moment(entry.endDate).format('YYYY-MM-DD');
        
        return formattedDate === entryStartDate || 
               formattedDate === entryEndDate || 
               (formattedDate > entryStartDate && formattedDate < entryEndDate);
      });
      
      // console.log(`Found ${vacationMatches.length} vacation entries for ${formattedDate}`);
      vacationMatches.forEach(entry => matchingEntries.push({ type: 'vacation', entry }));
    }
    
    if (isAwaiting) {
      const awaitingMatches = awaitingEntries.filter(entry => {
        const entryStartDate = moment(entry.startDate).format('YYYY-MM-DD');
        const entryEndDate = moment(entry.endDate).format('YYYY-MM-DD');
        
        return formattedDate === entryStartDate || 
               formattedDate === entryEndDate || 
               (formattedDate > entryStartDate && formattedDate < entryEndDate);
      });
      
      // console.log(`Found ${awaitingMatches.length} awaiting entries for ${formattedDate}`);
      awaitingMatches.forEach(entry => matchingEntries.push({ type: 'awaiting', entry }));
    }
    
    if (isSecondoption) {
      const secondoptionMatches = secondoptionEntries.filter(entry => {
        const entryStartDate = moment(entry.startDate).format('YYYY-MM-DD');
        const entryEndDate = moment(entry.endDate).format('YYYY-MM-DD');
        
        return formattedDate === entryStartDate || 
               formattedDate === entryEndDate || 
               (formattedDate > entryStartDate && formattedDate < entryEndDate);
      });
      
      // console.log(`Found ${secondoptionMatches.length} secondoption entries for ${formattedDate}`);
      secondoptionMatches.forEach(entry => matchingEntries.push({ type: 'secondoption', entry }));
    }
    
    // console.log(`Found ${matchingEntries.length} total matching entries for ${formattedDate}`);
    
    if (matchingEntries.length === 0) {
      return null;
    }
    
    // Sort by addedAt timestamp (most recent first)
    matchingEntries.sort((a, b) => moment(b.entry.addedAt).valueOf() - moment(a.entry.addedAt).valueOf());
    
    const latestEntry = matchingEntries[0];
    // console.log(`Latest entry for ${formattedDate} is type: ${latestEntry.type}, added at: ${moment(latestEntry.entry.addedAt).format('YYYY-MM-DD HH:mm:ss')}`);
    
    return latestEntry;
  };

  // Function to determine the style and shift for a day in the calendar
  const getDayStyleAndShift = (day) => {
    const formattedDate = moment(day).format('YYYY-MM-DD');
    // console.log(`Getting style for day: ${formattedDate}`);
    
    // Find all matching entries for this date (including dates within ranges)
    const matchingAvail = availabilities.filter(entry => {
      const startDate = moment(entry.startDate).format('YYYY-MM-DD');
      const endDate = moment(entry.endDate).format('YYYY-MM-DD');
      return formattedDate === startDate || 
             formattedDate === endDate || 
             (formattedDate > startDate && formattedDate < endDate);
    });
    
    const matchingVacation = vacations.filter(entry => {
      const startDate = moment(entry.startDate).format('YYYY-MM-DD');
      const endDate = moment(entry.endDate).format('YYYY-MM-DD');
      return formattedDate === startDate || 
             formattedDate === endDate || 
             (formattedDate > startDate && formattedDate < endDate);
    });
    
    const matchingAwaiting = awaitings.filter(entry => {
      const startDate = moment(entry.startDate).format('YYYY-MM-DD');
      const endDate = moment(entry.endDate).format('YYYY-MM-DD');
      return formattedDate === startDate || 
             formattedDate === endDate || 
             (formattedDate > startDate && formattedDate < endDate);
    });
    
    const matchingSecondoption = secondoptions.filter(entry => {
      const startDate = moment(entry.startDate).format('YYYY-MM-DD');
      const endDate = moment(entry.endDate).format('YYYY-MM-DD');
      return formattedDate === startDate || 
             formattedDate === endDate || 
             (formattedDate > startDate && formattedDate < endDate);
    });
    
    // Find the latest entry across all types
    const allMatches = [];
    
    matchingAvail.forEach(entry => allMatches.push({ type: 'available', entry }));
    matchingVacation.forEach(entry => allMatches.push({ type: 'vacation', entry }));
    matchingAwaiting.forEach(entry => allMatches.push({ type: 'awaiting', entry }));
    matchingSecondoption.forEach(entry => allMatches.push({ type: 'secondoption', entry }));
    
    if (allMatches.length === 0) {
      // console.log(`No matching entries for ${formattedDate}, using default style`);
      return { style: { backgroundColor: "white", opacity: 1, width: '70px' }, shift: 'full-day' };
    }
    
    // Sort by addedAt timestamp (most recent first) - this ensures the last added entry takes precedence
    // BUT FIRST: Fix any future timestamps (Firebase issue)
    const now = moment();
    const twoDaysAgo = moment().subtract(2, 'days');
    
    allMatches.forEach(match => {
      const originalTimestamp = match.entry.addedAt;
      const entryTime = moment(originalTimestamp);
      
      // If timestamp is in the future, it's a Firebase issue - replace with 2 days ago
      if (entryTime.isAfter(now)) {
        // console.log(`🚨 FIREBASE ISSUE: Future timestamp detected for ${match.type}: ${originalTimestamp} -> Correcting to 2 days ago`);
        match.entry.addedAt = twoDaysAgo.toISOString();
        match.correctedTimestamp = true;
      }
    });
    
    allMatches.sort((a, b) => moment(b.entry.addedAt).valueOf() - moment(a.entry.addedAt).valueOf());
    
    const latestEntry = allMatches[0];
    const shift = latestEntry.entry.shift || 'full-day';
    
    // console.log(`Latest entry for ${formattedDate} is ${latestEntry.type}, shift: ${shift}, added at: ${moment(latestEntry.entry.addedAt).format('YYYY-MM-DD HH:mm:ss')}`);
    
    // Apply styles based on the most recently added entry type
    let backgroundColor;
    switch (latestEntry.type) {
      case 'available':
        backgroundColor = "#2ACEF1";
        break;
      case 'vacation':
        backgroundColor = "#F12A5D";
        break;
      case 'awaiting':
        backgroundColor = "#FFA500";
        break;
      case 'secondoption':
        backgroundColor = "#FF69B4"; // Pink color
        break;
      default:
        backgroundColor = "white";
    }
    
    return { 
      style: { backgroundColor, opacity: 1, width: '70px' }, 
      shift 
    };
  };

  // Backward compatibility - keep getDayStyle for existing code
  const getDayStyle = (day) => {
    return getDayStyleAndShift(day).style;
  };

  const renderNoteIcon = (day) => {
    const formattedDay = moment(day).format('YYYY-MM-DD');
    const hasNotes = notes.some((note) => note.date === formattedDay);

    const iconStyle = hasNotes
      ? { backgroundColor: "#F12A5D", padding: "1px", borderRadius: "50%" }
      : {};

    return (
      <div className="early-shifts-img-cont">
        <img
          src={notesicon}
          className="early-late-icon"
          style={iconStyle}
          onClick={() => openNotesModal(day)}
        />
      </div>
    );
  };

  const getBorderRadius = (set) => {
    switch (set) {
      case "On Model":
        return "10px";
      case "on Produkt":
        return "0px";
      default:
        return "5px"; // Default border radius
    }
  };

  const getStatus = (status) => {
    switch (status) {
      case "confirmed":
        return "#15a326";
      case "new order":
        return "#ffffff";
      case "requested":
        return "#FF8806";
      case "talentaccepted":
        return "#3de651";
      case "declined request":
        return "#ff3c00";
      case "optioned":
        return "#0060ff";
      case "optionSolved":
        return "#FFFF00";
      case "hold":
        return "#800080";
      case "lano":
        return "#116c11";
      case "Booked":
        return "#ADD8E6";
      default:
        return "#606060";
    }
  };

  // Helper function to extract position/function from booking
  const getPositionFunction = (booking) => {
    if (!booking) return 'N/A';
    
    // Try position object first
    if (booking.position) {
      if (typeof booking.position === 'object') {
        const positionValue = booking.position.label || booking.position.value || booking.position.name;
        if (positionValue) return positionValue;
      }
      // If position is a string, try to parse it as JSON
      if (typeof booking.position === 'string' && booking.position.trim() !== '') {
        // Try parsing as JSON first
        try {
          const parsed = JSON.parse(booking.position);
          if (typeof parsed === 'object') {
            const parsedValue = parsed.label || parsed.value || parsed.name;
            if (parsedValue) return parsedValue;
            return booking.position; // Return original if parsed object has no label/value/name
          }
          return parsed;
        } catch (e) {
          // If parsing fails, return the string as-is (it's already a function name)
          return booking.position;
        }
      }
    }
    
    // Try other field variations
    if (booking.funktion) return booking.funktion;
    if (booking.Funktion) return booking.Funktion;
    if (booking.function) return booking.function;
    if (booking.position_name) return booking.position_name;
    
    // Last resort: try to extract from title (format: "Function - Talent Name")
    if (booking.title && typeof booking.title === 'string') {
      const titleParts = booking.title.split(' - ');
      if (titleParts.length > 0 && titleParts[0].trim()) {
        return titleParts[0].trim();
      }
    }
    
    return 'N/A';
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".toltip")) {
        setTooltipStates({});
      }
      // Close date picker if clicking outside
      if (showToDateCalendar && !event.target.closest(".date-picker-container")) {
        setShowToDateCalendar(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showToDateCalendar]);

  const toggleTooltip = (bookingId) => {
    setTooltipStates((prevStates) => ({
      ...prevStates,
      [bookingId]: !prevStates[bookingId],
    }));
  };

  const isEarlyShift = (booking) => {
    if (!booking || !booking.start_time) return false;
    
    try {
      const [hours, minutes] = booking.start_time.split(":").map(Number);
      return hours < 12 || (hours === 12 && minutes === 0);
    } catch (error) {
      console.error("Error parsing start time:", error);
      return false;
    }
  };

  const generateWeekGrid = () => {
    let grid = [];
    let currentDate = new Date(dateRange.start);

    for (let i = 0; i < 7; i++) {
      const date = new Date(currentDate);
      grid.push(date);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return grid;
  };

  const weekGrid = generateWeekGrid();

  const openModal = (day) => {
    setSelectedDay(day);
    setNewAvailability({ startDate: moment(day).format('YYYY-MM-DD'), endDate: '' });
    setAvailabilityShift('full-day'); // Reset to default
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setNewAvailability({ startDate: '', endDate: '' });
    setAvailabilityShift('full-day'); // Reset to default
    setIsMultipleDays(false);
    setShowToDateCalendar(false);
  };

  // Generate calendar dates for date picker
  const generateCalendarDates = () => {
    const startDate = newAvailability.startDate ? moment(newAvailability.startDate) : moment();
    const currentMonth = startDate.clone().startOf('month');
    const startOfCalendar = currentMonth.clone().startOf('isoWeek'); // Use isoWeek to start on Monday
    const endOfCalendar = currentMonth.clone().endOf('month').endOf('isoWeek'); // Use isoWeek to end on Sunday
    
    const dates = [];
    let current = startOfCalendar.clone();
    
    while (current.isSameOrBefore(endOfCalendar)) {
      dates.push(current.clone());
      current.add(1, 'day');
    }
    
    return dates;
  };

  // Handle date selection from calendar
  const handleCalendarDateSelect = (selectedMoment) => {
    const selectedDateStr = selectedMoment.format('YYYY-MM-DD');
    const startDateStr = newAvailability.startDate;
    
    // Only allow dates on or after the start date
    if (selectedMoment.isSameOrAfter(moment(startDateStr))) {
      setNewAvailability(prev => ({ ...prev, endDate: selectedDateStr }));
      setShowToDateCalendar(false);
    }
  };

  const handleAvailabilityChange = (e) => {
    const { name, value } = e.target;
    setNewAvailability((prev) => ({ ...prev, [name]: value }));

  };

  const openNotesModal = async (day) => {
    setSelectedDay(day);
    setIsModalNotesOpen(true);
    const formattedDay = moment(day).format('YYYY-MM-DD');
    const notesForSelectedDay = notes.filter(note => note.date === formattedDay);
    setNotesForDay(notesForSelectedDay);
  };

  const closeNotesModal = () => {
    setIsModalNotesOpen(false);
    setNotesForDay([]);
    setNewNote("");
  };

  const openBookingModal = (day, talentId, talentfunktion, talentName, talentSet, talentEmail, talentRate) => {
    setSelectedDate(day);
    setSelectedTalentId(talentId);
    setSelectedTalentPosition(talentfunktion);
    setSelectedTalentName(talentName);
    setSelectedTalentSet(talentSet);
    setSelectedTalentEmail(talentEmail);
    setSelectedTalentRate (talentRate)
  };

  useEffect(() => {
    if (selectedDate && selectedTalentId && selectedTalentPosition && selectedTalentName &&selectedTalentSet && selectedTalentEmail, selectedTalentRate) {
      // console.log(selectedTalentSet);
      setIsModalBookingOpen(true);
    }
  }, [selectedDate, selectedTalentId, selectedTalentPosition, selectedTalentName, selectedTalentSet, selectedTalentEmail, selectedTalentRate]);

  const closeBookingModal = () => {
    setIsModalBookingOpen(false);
    setSelectedDate('');
    setSelectedTalentId('');
    setSelectedTalentPosition('');
    setSelectedTalentName('');
    setSelectedTalentSet('');
    setSelectedTalentEmail('');
    setSelectedTalentRate ('')
  };

  const handleNoteChange = (e) => {
    setNewNote(e.target.value);
  };

  const closeNotizModal = () => {
    setIsNotizModalOpen(false);
  };

  return (
    <div className="tlistcal-week-grid">
      <div style={{ flexBasis: "400px", fontWeight: "600",  }}>
        {talent.Name}{" "}
        <div style={{ fontWeight: "400", fontSize: "12px" }}>
          {talent.Funktion} | {talent.Bereich}

        </div>
        <div style={{ fontWeight: "400", fontSize: "12px" }}>
 
          {talent.E_Mail}
        </div>
        <div style={{ fontWeight: "400", fontSize: "12px" }}>
 
          {talent.Telefon}
        </div>
        <div style={{ fontWeight: "400", fontSize: "12px" }}>
        {talent.Tagessatz}€/day
        </div>
        </div>

<div style={{
  display: "flex",
  flexDirection: "column",
  gap: "3px",
  marginBottom: "10px",
  width: "60px"
}}>
  {/* Setcast Notes Button */}
  <div style={{ 
    display: "flex", 
    flexDirection: "column",
    height: "22px", 
    backgroundColor: "#000", 
    width: "100%", 
    justifyContent: "center", 
    alignItems: "center", 
    padding: "3px", 
    textAlign: "center", 
    position: "relative",
    cursor: isLoadingNotes ? "wait" : "pointer",
    transition: "all 0.2s ease",
    opacity: isLoadingNotes ? 0.7 : 1,
    fontSize: "10px",
    fontWeight: "600",
    color: "#fff",
  }} 
  onClick={openNotizModal}>
    {isLoadingNotes ? (
      <ClipLoader 
        color="#ffffff" 
        size={12} 
        loading={true}
      />
    ) : (
      'NOTES'
    )}
  </div>

  {/* Product Lead Notes Button (Read-only) */}
  <div style={{ 
    display: "flex", 
    flexDirection: "column",
    height: "22px", 
    backgroundColor: "#000", 
    width: "100%", 
    justifyContent: "center", 
    alignItems: "center", 
    padding: "3px", 
    textAlign: "center", 
    position: "relative",
    cursor: isLoadingLeadNotes ? "wait" : "pointer",
    transition: "all 0.2s ease",
    opacity: isLoadingLeadNotes ? 0.7 : 1,
    fontSize: "10px",
    fontWeight: "600",
    color: "#fff",
  }} 
  onClick={openLeadNotesModal}>
    {isLoadingLeadNotes ? (
      <ClipLoader 
        color="#ffffff" 
        size={12} 
        loading={true}
      />
    ) : (
      'LEAD'
    )}
  </div>
</div>

      {weekGrid.map((day, index) => {
        const { style, shift } = getDayStyleAndShift(day);
        return (
          <div
            key={index}
            className="tlistcal-item tlistcal-c-column"
          >
            <div style={{ display: 'flex', justifyContent: 'start', border: 'solid 1px', maxHeight: '25px', position: 'relative' }}>
              <div style={{ ...style, position: 'relative' }}>
                {/* Show shift icons on availability color */}
                {shift !== 'full-day' && (
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2,
                    pointerEvents: 'none'
                  }}>
                    {shift === 'early' && (
                      <img 
                        src={early} 
                        className="early-late-icon" 
                        style={{ 
                          width: '12px', 
                          height: '12px',
                          filter: 'brightness(0) invert(1) drop-shadow(0 1px 1px rgba(0,0,0,0.2))'
                        }}
                        alt="Early shift"
                      />
                    )}
                    {shift === 'late' && (
                      <img 
                        src={late} 
                        className="early-late-icon" 
                        style={{ 
                          width: '12px', 
                          height: '12px',
                          filter: 'brightness(0) invert(1) drop-shadow(0 1px 1px rgba(0,0,0,0.2))'
                        }}
                        alt="Late shift"
                      />
                    )}
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                {renderNoteIcon(day)}

                <div className="early-shifts-img-cont">
                  <img src={shiftsAvailability} className="early-late-icon" onClick={() => openModal(day)} />
                </div>
                <div className="early-shifts-img-cont"> 
                  <img src={plus} className="early-late-icon" onClick={() => openBookingModal(day, talent.id, talent.Funktion, talent.Name, talent.Bereich, talent.E_Mail, talent.Tagessatz)} />
                </div>
              </div>
            </div>

          <div className="early-shifts">
            <div style={{ minHeight: "20px" }}>
              {bookings
                .filter(
                  (booking) =>
                    new Date(booking.start_date).toDateString() ===
                      day.toDateString() && isEarlyShift(booking)
                )
                .map((filteredBooking) => (
                  <div
                    key={filteredBooking.id}
                    className="tlistcal-booking"
                    style={{
                      borderRadius: getBorderRadius(filteredBooking.set),
                    }}
                    onClick={() => toggleTooltip(filteredBooking.id)}
                  >
                    <div style={{
                      height: 15, width: "100%", fontSize: 12, overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "flex",
                      alignItems: "center",
                      paddingLeft: "5px",
                      paddingTop: "0px",
                      paddingBottom: "0px"
                    }}>
                      <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                        {filteredBooking?.client?.label || filteredBooking?.client?.name || 'No Client'}
                      </span>
                      <div
                        style={{
                          height: "5px",
                          width: "5px",
                          padding: "7px",
                          backgroundColor: getStatus(filteredBooking.status),
                          borderRadius: "50%",
                          display: "inline-block",
                          float: "right",
                        }}
                      />
                    </div>

                    {tooltipStates[filteredBooking.id] && (
                      <div
                        className="toltip"
                        style={{
                          position: "absolute",
                          width: "600px",
                          height: "auto",
                          top: "10px",
                          left: "10px",
                          background: "#000000",
                          color: "#ffffff",
                          padding: "10px",
                          zIndex: 9999,
                          cursor: "auto",
                        }}
                        onClick={() => {
                          // console.log('🔍 Booking data for tooltip (early shift):', filteredBooking);
                          // console.log('🔍 Position field:', filteredBooking.position);
                          // console.log('🔍 Extracted function:', getPositionFunction(filteredBooking));
                          setshowDetail(!showDetail);
                        }}
                      >
                        <span
                          style={{
                            position: "relative",
                            fontWeight: "600",
                            color: "#ffffff",
                            whiteSpace: "pre-line",
                          }}
                        >
                          <div className="calendar-inner-cell-container">
                            <div>
                              <div
                                style={{
                                  fontWeight: "300",
                                  fontSize: ".8rem",
                                  marginBottom: "2px",
                                }}
                              >
                                {` Client `}
                              </div>
                              {` ${filteredBooking.client?.label || filteredBooking.client?.name || filteredBooking.client || 'N/A'} `}
                              <div
                                style={{
                                  fontWeight: "300",
                                  fontSize: ".8rem",
                                  marginTop: "5px",
                                  marginBottom: "2px",
                                }}
                              >
                                {` Talent `}
                              </div>
                              {` ${filteredBooking.talent_name || filteredBooking.talentName || filteredBooking.talent?.name || 'N/A'} `}
                              <div
                                style={{
                                  fontWeight: "300",
                                  fontSize: ".8rem",
                                  marginTop: "5px",
                                  marginBottom: "2px",
                                }}
                              >
                                {` Status `}
                              </div>
                              {` ${filteredBooking.status || 'N/A'} `}
                              <div
                                style={{
                                  fontWeight: "300",
                                  fontSize: ".8rem",
                                  marginTop: "5px",
                                  marginBottom: "2px",
                                }}
                              >
                                {` Function `}
                              </div>
                              {` ${getPositionFunction(filteredBooking)} `}
                            </div>
                            <div>
                              <div
                                style={{
                                  fontWeight: "300",
                                  fontSize: ".8rem",
                                  marginBottom: "2px",
                                }}
                              >
                                {` Set `}
                              </div>
                              {` ${filteredBooking.set || 'N/A'} `}
                              <div
                                style={{
                                  fontWeight: "300",
                                  fontSize: ".8rem",
                                  marginTop: "5px",
                                  marginBottom: "2px",
                                }}
                              >
                                {` Kpi `}
                              </div>
                              {` ${filteredBooking.kpi !== null && filteredBooking.kpi !== undefined ? filteredBooking.kpi : 'N/A'} `}
                              <div
                                style={{
                                  fontWeight: "300",
                                  fontSize: ".8rem",
                                  marginTop: "5px",
                                  marginBottom: "2px",
                                }}
                              >
                                {` Start time `}
                              </div>
                              {` ${filteredBooking.start_date ? moment(filteredBooking.start_date).format("DD-MM-YYYY") : 'N/A'} `}
                              <div
                                style={{
                                  fontWeight: "300",
                                  fontSize: ".8rem",
                                  marginTop: "5px",
                                  marginBottom: "2px",
                                }}
                              >
                                {` S.Info `}
                              </div>
                              {` ${filteredBooking.sonstige_info || filteredBooking.SonstigeInfo || filteredBooking.sonstigeInfo || 'N/A'} `}
                            </div>
                            <div style={{ flexGrow: "1" }}>
                              <div
                                style={{
                                  fontWeight: "300",
                                  fontSize: ".8rem",
                                  marginBottom: "2px",
                                }}
                              >
                                {` Proposed by `}
                              </div>
                              {` ${filteredBooking.proposed_by || filteredBooking.proposedBy || 'N/A'} `}
                            </div>
                            
                          </div>
                        </span>
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>

          <div className="late-shifts" style={{ minHeight: "25px" }}>
            {bookings
              .filter(
                (booking) =>
                  new Date(booking.start_date).toDateString() ===
                    day.toDateString() && !isEarlyShift(booking)
              )
              .map((filteredBooking) => (
                <div
                  key={filteredBooking.id}
                  className="tlistcal-booking"
                  style={{
                    borderRadius: getBorderRadius(filteredBooking.set),
                  }}
                  onClick={() => toggleTooltip(filteredBooking.id)}
                >
                  <div style={{ 
                    height: 25, 
                    width: "100%", 
                    fontSize: 10, 
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: "5px",
                    paddingTop: "0px",
                    paddingBottom: "0px"
                  }}>
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {filteredBooking?.client?.label || filteredBooking?.client?.name || 'No Client'}
                    </span>
                    <div
                      style={{
                        height: "7px",
                        width: "7px",
                        padding: "10px",
                        backgroundColor: getStatus(filteredBooking.status),
                        borderRadius: "50%",
                        display: "inline-block",
                        float: "right",
                      }}
                    />
                  </div>

                  {tooltipStates[filteredBooking.id] && (
                    <div
                      className="toltip"
                      style={{
                        position: "absolute",
                        width: "600px",
                        height: "auto",
                        top: "10px",
                        left: "10px",
                        background: "#000000",
                        color: "#ffffff",
                        padding: "10px",
                        zIndex: 9999,
                        cursor: "auto",
                      }}
                      onClick={() => {
                        // console.log('🔍 Booking data for tooltip (late shift):', filteredBooking);
                        // console.log('🔍 Position field:', filteredBooking.position);
                        // console.log('🔍 Extracted function:', getPositionFunction(filteredBooking));
                        setshowDetail(!showDetail);
                      }}
                    >
                      <span
                        style={{
                          position: "relative",
                          fontWeight: "600",
                          color: "#ffffff",
                          whiteSpace: "pre-line",
                        }}
                      >
                        <div className="calendar-inner-cell-container">
                          <div>
                            <div
                              style={{
                                fontWeight: "300",
                                fontSize: ".8rem",
                                marginBottom: "2px",
                              }}
                            >
                              {` Client `}
                            </div>
                            {` ${filteredBooking.client?.label || filteredBooking.client?.name || filteredBooking.client || 'N/A'} `}
                            <div
                              style={{
                                fontWeight: "300",
                                fontSize: ".8rem",
                                marginTop: "5px",
                                marginBottom: "2px",
                              }}
                            >
                              {` Talent `}
                            </div>
                            {` ${filteredBooking.talent_name || filteredBooking.talentName || filteredBooking.talent?.name || 'N/A'} `}
                            <div
                              style={{
                                fontWeight: "300",
                                fontSize: ".8rem",
                                marginTop: "5px",
                                marginBottom: "2px",
                              }}
                            >
                              {` Status `}
                            </div>
                            {` ${filteredBooking.status || 'N/A'} `}
                            <div
                              style={{
                                fontWeight: "300",
                                fontSize: ".8rem",
                                marginTop: "5px",
                                marginBottom: "2px",
                              }}
                            >
                              {` Function `}
                            </div>
                            {` ${getPositionFunction(filteredBooking)} `}
                          </div>
                          <div>
                            <div
                              style={{
                                fontWeight: "300",
                                fontSize: ".8rem",
                                marginBottom: "2px",
                              }}
                            >
                              {` Set `}
                            </div>
                            {` ${filteredBooking.set || 'N/A'} `}
                            <div
                              style={{
                                fontWeight: "300",
                                fontSize: ".8rem",
                                marginTop: "5px",
                                marginBottom: "2px",
                              }}
                            >
                              {` Kpi `}
                            </div>
                            {` ${filteredBooking.kpi !== null && filteredBooking.kpi !== undefined ? filteredBooking.kpi : 'N/A'} `}
                            <div
                              style={{
                                fontWeight: "300",
                                fontSize: ".8rem",
                                marginTop: "5px",
                                marginBottom: "2px",
                              }}
                            >
                              {` Start time `}
                            </div>
                            {` ${filteredBooking.start_date ? moment(filteredBooking.start_date).format("DD-MM-YYYY") : 'N/A'} `}
                            <div
                              style={{
                                fontWeight: "300",
                                fontSize: ".8rem",
                                marginTop: "5px",
                                marginBottom: "2px",
                              }}
                            >
                              {` S.Info `}
                            </div>
                            {` ${filteredBooking.sonstige_info || filteredBooking.SonstigeInfo || filteredBooking.sonstigeInfo || 'N/A'} `}
                          </div>
                          <div style={{ flexGrow: "1" }}>
                            <div
                              style={{
                                fontWeight: "300",
                                fontSize: ".8rem",
                                marginTop: "5px",
                                marginBottom: "2px",
                              }}
                            >
                              {` Created At `}
                            </div>
                            {` ${filteredBooking.created_at ? moment(filteredBooking.created_at).format('DD-MM-YYYY HH:mm') : 'N/A'} `}
                            <div
                              style={{
                                fontWeight: "300",
                                fontSize: ".8rem",
                                marginTop: "5px",
                                marginBottom: "2px",
                              }}
                            >
                              {` Updated At `}
                            </div>
                            {` ${filteredBooking.updated_at ? moment(filteredBooking.updated_at).format('DD-MM-YYYY HH:mm') : 'N/A'} `}
                            <div
                              style={{
                                fontWeight: "300",
                                fontSize: ".8rem",
                                marginTop: "5px",
                                marginBottom: "2px",
                              }}
                            >
                              {` Status Updated At `}
                            </div>
                            {` ${filteredBooking.status_updated_at ? moment(filteredBooking.status_updated_at).format('DD-MM-YYYY HH:mm') : 'N/A'} `}
                          </div>
                        </div>
                      </span>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
        );
      })}

      <Modal
        isOpen={isModalNotesOpen}
        onRequestClose={closeNotesModal}
        contentLabel="Notes Modal"
        style={{
          overlay: {
            zIndex: 9999,
          },
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
          },
        }}
       
      >
     
        <h2>Notes for {selectedDay && selectedDay.toDateString()}</h2>
  <div>
    {notesForDay.map((note, index) => {
    // Get date from ISO string or timestamp
    const noteDate = note.created_at ? new Date(note.created_at) : new Date();

    // Format the date as "dd.mm.yy"
    const formattedDate = noteDate.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    });

    // Format the time as "hh:mm"
    const formattedTime = noteDate.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    });

      return (
        <div key={index}>
          <strong>{note.user || 'User'}:</strong> {note.notes || note.content} | {formattedDate}-{formattedTime}
        </div>
      );
    })}
  </div>
        <div className="textarea">
          <textarea
            value={newNote}
            onChange={handleNoteChange}
            placeholder="Add a note"
          />
        </div>
        <button className="button" onClick={saveNote}>Save Note</button>
        <button className="button" onClick={closeNotesModal}>Close</button>
      </Modal>
  
      <BookingModal
        onClose={closeBookingModal}
        selectedDate={selectedDate}
        selectedTalentId={selectedTalentId}
        selectedFunktion={selectedTalentPosition}
        selectedTalentName={selectedTalentName}
        selectedTalentSet={selectedTalentSet}
        selectedTalentEmail={selectedTalentEmail}
        talentRate={selectedTalentRate}
        isOpen={isModalBookingOpen}
      />
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        contentLabel="Add Availability"
        style={{
          overlay: {
            zIndex: 9999,
          },
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
          },
        }}
      >
        <div style={{ padding: '20px' }}>
          <h2>Add Availability for {selectedDay && selectedDay.toDateString()}</h2>
          <div style={{ display: "grid" }}>
            <label style={{ marginTop: '10px', marginBottom: '10px' }}>
              From Date:
            </label>
            <input
              type="date"
              name="startDate"
              value={newAvailability.startDate}
              onChange={handleAvailabilityChange}
            />

            <label style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={isMultipleDays}
                onChange={(e) => setIsMultipleDays(e.target.checked)}
              />
              Multiple days
            </label>

            {isMultipleDays && (
              <>
                <label style={{ marginTop: '10px', marginBottom: '5px' }}>
                  To Date:
                </label>
                <div className="date-picker-container" style={{ position: 'relative' }}>
                  <input
                    type="date"
                    name="endDate"
                    value={newAvailability.endDate}
                    onChange={handleAvailabilityChange}
                    min={newAvailability.startDate} // Ensure end date is not before start date
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                    onClick={() => setShowToDateCalendar(true)}
                  />
                  
                  {/* Calendar Picker Popup */}
                  {showToDateCalendar && (
                    <div className="date-picker-popup" style={{
                      position: 'absolute',
                      top: '100%',
                      left: '0',
                      zIndex: 1000,
                      backgroundColor: 'white',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      padding: '10px',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      minWidth: '280px'
                    }}>
                      {/* Calendar Header */}
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px',
                        fontWeight: 'bold'
                      }}>
                        <span>
                          {newAvailability.startDate ? 
                            moment(newAvailability.startDate).format('MMMM YYYY') : 
                            moment().format('MMMM YYYY')
                          }
                        </span>
                        <button 
                          onClick={() => setShowToDateCalendar(false)}
                          style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '16px',
                            cursor: 'pointer'
                          }}
                        >
                          ×
                        </button>
                      </div>
                      
                      {/* Calendar Grid */}
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: '2px',
                        textAlign: 'center'
                      }}>
                        {/* Day headers */}
                        {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                          <div key={day} style={{
                            padding: '5px',
                            fontSize: '12px',
                            fontWeight: 'bold',
                            color: '#666'
                          }}>
                            {day}
                          </div>
                        ))}
                        
                        {/* Calendar dates */}
                        {generateCalendarDates().map((date, index) => {
                          const isDisabled = date.isBefore(moment(newAvailability.startDate));
                          const isSelected = date.format('YYYY-MM-DD') === newAvailability.endDate;
                          const isToday = date.isSame(moment(), 'day');
                          
                          return (
                            <button
                              key={index}
                              onClick={() => !isDisabled && handleCalendarDateSelect(date)}
                              disabled={isDisabled}
                              style={{
                                padding: '8px 4px',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                backgroundColor: isSelected ? '#007bff' : 
                                               isToday ? '#e9ecef' : 
                                               'transparent',
                                color: isSelected ? 'white' : 
                                       isDisabled ? '#ccc' : 
                                       isToday ? '#007bff' : 
                                       'black',
                                fontSize: '12px',
                                opacity: isDisabled ? 0.5 : 1
                              }}
                            >
                              {date.date()}
                            </button>
                          );
                        })}
                      </div>
                      
                      {/* Quick select buttons */}
                      <div style={{
                        marginTop: '10px',
                        display: 'flex',
                        gap: '5px',
                        flexWrap: 'wrap'
                      }}>
                        <button
                          onClick={() => handleCalendarDateSelect(moment(newAvailability.startDate).add(6, 'days'))}
                          style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            border: '1px solid #ccc',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            backgroundColor: 'white'
                          }}
                        >
                          +7 days
                        </button>
                        <button
                          onClick={() => handleCalendarDateSelect(moment(newAvailability.startDate).add(13, 'days'))}
                          style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            border: '1px solid #ccc',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            backgroundColor: 'white'
                          }}
                        >
                          +14 days
                        </button>
                        <button
                          onClick={() => handleCalendarDateSelect(moment(newAvailability.startDate).add(29, 'days'))}
                          style={{
                            padding: '4px 8px',
                            fontSize: '12px',
                            border: '1px solid #ccc',
                            borderRadius: '3px',
                            cursor: 'pointer',
                            backgroundColor: 'white'
                          }}
                        >
                          +30 days
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            <div style={{ marginTop: '20px', marginBottom: '10px' }}>
              <strong>Status:</strong>
            </div>

            <label>
              <input
                type="radio"
                name="status"
                value="available"
                checked={availabilityStatus === 'available'}
                onChange={(e) => setAvailabilityStatus(e.target.value)}
              />
              Available
            </label>
            <label>
              <input
                type="radio"
                name="status"
                value="not-available"
                checked={availabilityStatus === 'not-available'}
                onChange={(e) => setAvailabilityStatus(e.target.value)}
              />
              Not Available
            </label>
            <label>
              <input
                type="radio"
                name="status"
                value="awaiting"
                checked={availabilityStatus === 'awaiting'}
                onChange={(e) => setAvailabilityStatus(e.target.value)}
              />
              Awaiting
            </label>
            <label>
              <input
                type="radio"
                name="status"
                value="secondoption"
                checked={availabilityStatus === 'secondoption'}
                onChange={(e) => setAvailabilityStatus(e.target.value)}
              />
              Second Option
            </label>

            <div style={{ marginTop: '20px', marginBottom: '10px' }}>
              <strong>Shift:</strong>
            </div>

            <label>
              <input
                type="radio"
                name="shift"
                value="full-day"
                checked={availabilityShift === 'full-day'}
                onChange={(e) => setAvailabilityShift(e.target.value)}
              />
              Full Day
            </label>
            <label>
              <input
                type="radio"
                name="shift"
                value="early"
                checked={availabilityShift === 'early'}
                onChange={(e) => setAvailabilityShift(e.target.value)}
              />
              Early Shift
            </label>
            <label>
              <input
                type="radio"
                name="shift"
                value="late"
                checked={availabilityShift === 'late'}
                onChange={(e) => setAvailabilityShift(e.target.value)}
              />
              Late Shift
            </label>

            <div style={{ display: 'flex', paddingTop: '20px' }}>
              <button className="button" onClick={saveAvailability}>Save Availability</button>
              <button className="button" onClick={closeModal}>Close</button>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={isNotizModalOpen}
        onRequestClose={closeNotizModal}
        contentLabel="Talent Notes"
        ariaHideApp={false}
        style={{
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            minWidth: "500px",
            maxWidth: "800px",
            maxHeight: "80vh",
            overflow: "auto"
          },
        }}
      >
        <div style={{ padding: "20px" }}>
          <h2>Notes for {talent.Name}</h2>
          
          {/* Existing Notes Display */}
          <div style={{ 
            whiteSpace: "pre-wrap",
            marginTop: "10px",
            marginBottom: "20px",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "4px",
            backgroundColor: "#f9f9f9"
          }}>
            {notizSetcast || talent.Notiz_Setcast || 'No notes available'}
          </div>

          {/* Add New Note Section */}
          <div style={{ marginTop: "20px" }}>
            <h3>Add New Note</h3>
            <textarea
              value={editedNotiz}
              onChange={(e) => setEditedNotiz(e.target.value)}
              style={{
                width: "100%",
                minHeight: "100px",
                padding: "8px",
                marginBottom: "10px",
                borderRadius: "4px",
                border: "1px solid #ccc"
              }}
              placeholder="Type your new note here..."
            />
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button 
                className="button"
                onClick={addNewNote}
                disabled={!editedNotiz.trim()}
              >
                Add Note
              </button>
              <button 
                className="button"
                onClick={() => {
                  setEditedNotiz('');
                  setIsNotizModalOpen(false);
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Product Lead Notes Modal - Minimalistic */}
      <Modal
        isOpen={isLeadNotesModalOpen}
        onRequestClose={closeLeadNotesModal}
        contentLabel="Product Lead Notes"
        ariaHideApp={false}
        style={{
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            minWidth: "400px",
            maxWidth: "600px",
            maxHeight: "70vh",
            overflow: "auto",
            border: "1px solid #ccc",
            padding: "15px",
            borderRadius: "4px"
          }
        }}
      >
        <div style={{ fontSize: "14px", lineHeight: "1.4", whiteSpace: "pre-wrap" }}>
          {notizLead || 'No product lead notes available'}
        </div>
        
        <div style={{ marginTop: "15px", textAlign: "right" }}>
          <button 
            onClick={closeLeadNotesModal}
            style={{
              background: "none",
              border: "1px solid #ccc",
              padding: "5px 12px",
              cursor: "pointer",
              borderRadius: "3px",
              fontSize: "12px"
            }}
          >
            Close
          </button>
        </div>
      </Modal>
    </div>
  );
};

// Helper function to extract client name from booking (defined outside component for reuse)
const getClientName = (booking) => {
  if (!booking) return null;
  // Try object formats first
  if (booking.client && typeof booking.client === 'object') {
    return booking.client.label || booking.client.name || booking.client.value || null;
  }
  // Try string formats
  if (typeof booking.client === 'string' && booking.client.trim() !== '') {
    return booking.client;
  }
  // Try alternative field names
  if (booking.client_name) {
    return booking.client_name;
  }
  return null;
};

// Main Component
const TalentListCalendarmain = () => {
  const { dateRange, selectedClient, setSelectedClient, selectedStatus, setSelectedStatus, setScheduleDataMap } = useCalendar();

  const [talents, setTalents] = useState([]);
  const [selectedTalents, setSelectedTalents] = useState([]);
  const [clients, setClients] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedSet, setSelectedSet] = useState(null);
  const [selectedFunc, setSelectedFunc] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [lastDoc, setLastDoc] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNoBookingsPopup, setShowNoBookingsPopup] = useState(false);
  const [filterGoodCompanies, setFilterGoodCompanies] = useState([]);
  const [filterBadCompanies, setFilterBadCompanies] = useState([]);
  const [filterTryoutCompanies, setFilterTryoutCompanies] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Talent Groups state
  const [talentGroups, setTalentGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]); // Changed to array for multi-select
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupTalents, setNewGroupTalents] = useState([]);

  const bookingStatusOptions = [
    { value: 'Booked', label: 'booked / Lano task created' },
    { value: 'confirmed', label: 'confirmed' },
    { value: 'new order', label: 'new order' },
    { value: 'requested', label: 'requested' },
    { value: 'declined request', label: 'declined request' },
    { value: 'optioned', label: 'optioned' },
    { value: 'optionSolved', label: 'option released' },
    { value: 'talentOnHold', label: '1. Option' },
  ];

  // State for hover tooltip and expanded view
  const [hoveredTalentSelect, setHoveredTalentSelect] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [isTalentListExpanded, setIsTalentListExpanded] = useState(false);

  // Custom ValueContainer component to limit visible items
  const CustomValueContainer = ({ children, ...props }) => {
    const MAX_VISIBLE = 1;
    const selectedValues = props.selectProps.value || [];
    const remainingCount = selectedValues.length - MAX_VISIBLE;
    const shouldShowAll = isTalentListExpanded || selectedValues.length <= MAX_VISIBLE;

    // Separate MultiValue components from Input/Placeholder
    const childrenArray = React.Children.toArray(children);
    const multiValues = [];
    const otherChildren = [];

    childrenArray.forEach((child) => {
      // Check if it's a MultiValue by looking at the props structure
      if (child && typeof child === 'object' && 'props' in child) {
        // MultiValue components typically have data prop
        if (child.props && child.props.data) {
          multiValues.push(child);
        } else {
          otherChildren.push(child);
        }
      } else {
        otherChildren.push(child);
      }
    });

    const visibleMultiValues = shouldShowAll ? multiValues : multiValues.slice(0, MAX_VISIBLE);

    const handleMouseEnter = (e) => {
      if (remainingCount > 0 && !shouldShowAll) {
        const rect = e.currentTarget.getBoundingClientRect();
        setTooltipPosition({
          top: rect.bottom + window.scrollY + 5,
          left: rect.left + window.scrollX
        });
        setHoveredTalentSelect(true);
      }
    };

    const handleMouseLeave = () => {
      setHoveredTalentSelect(false);
    };

    const handleClickMore = (e) => {
      e.stopPropagation();
      setIsTalentListExpanded(!isTalentListExpanded);
    };

    return (
      <components.ValueContainer {...props}>
        <div
          style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center', width: '100%' }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {visibleMultiValues}
          {remainingCount > 0 && (
            <div
              style={{
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                padding: '2px 8px',
                fontSize: '0.85em',
                color: '#495057',
                cursor: 'pointer',
                position: 'relative',
                height: '24px',
                display: 'flex',
                alignItems: 'center'
              }}
              onClick={handleClickMore}
            >
              {shouldShowAll ? `-${remainingCount} less` : `+${remainingCount} more`}
              {hoveredTalentSelect && remainingCount > 0 && !shouldShowAll && (
                <div
                  style={{
                    position: 'fixed',
                    top: `${tooltipPosition.top}px`,
                    left: `${tooltipPosition.left}px`,
                    backgroundColor: '#000',
                    color: '#fff',
                    padding: '10px',
                    borderRadius: '4px',
                    zIndex: 9999,
                    minWidth: '200px',
                    maxWidth: '400px',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    whiteSpace: 'normal',
                    wordBreak: 'break-word'
                  }}
                >
                  <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '12px' }}>
                    All Selected Talents ({selectedValues.length}):
                  </div>
                  {selectedValues.map((talent, index) => (
                    <div
                      key={talent.value || index}
                      style={{
                        padding: '4px 0',
                        fontSize: '11px',
                        borderBottom: index < selectedValues.length - 1 ? '1px solid #333' : 'none'
                      }}
                    >
                      {talent.label}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {otherChildren}
        </div>
      </components.ValueContainer>
    );
  };

  // Override customStyles to have consistent 200px minimum width
  const localCustomStyles = {
    ...customStyles,
    control: (provided, state) => ({
      ...provided,
      minWidth: '200px',
      borderColor: "black",
      borderWidth: "1px",
      boxShadow: "none",
      margin: "2px",
      borderRadius: "0px",
      color: "black",
      fontWeight: "400",
      height: "auto",
      textTransform: "uppercase",
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#f8f9fa',
      border: '1px solid #dee2e6',
      borderRadius: '4px',
      padding: '2px'
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: '#495057',
      fontSize: '0.85em'
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: '#dc3545',
      ':hover': {
        backgroundColor: 'transparent',
        color: '#dc3545'
      }
    }),
  };

  // Fetch locations from API
  const fetchLocations = async () => {
    try {
      const response = await locationService.getAllLocations(1, 1000); // Fetch all locations
      const locationsData = response?.data?.locations || response?.data || [];
      
      const formattedLocations = locationsData
        .map(location => ({
          value: location.short, // Use short code as value
          label: location.short, // Display only short code
          short: location.short
        }))
        .filter(location => location.value && location.short) // Filter out entries without short codes
        .sort((a, b) => a.label.localeCompare(b.label));
      
      setLocationOptions(formattedLocations);
      // console.log("Loaded location options:", formattedLocations);
    } catch (error) {
      console.error('Error fetching locations:', error);
      // Set fallback locations if API fails
      setLocationOptions([
        { value: 'HH', label: 'HH', short: 'HH' },
        { value: 'BER', label: 'BER', short: 'BER' },
        { value: 'MUC', label: 'MUC', short: 'MUC' }
      ]);
    }
  };

  // Fetch companies from API
  const fetchCompanies = async () => {
    try {
      const response = await companyService.getAllCompanies(1, 1000); // Fetch all companies
      const companiesData = response?.data?.companies || response?.data || [];
      
      const formattedCompanies = companiesData
        .map(company => ({
          value: company.id,
          label: company.name
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      
      setCompanyOptions(formattedCompanies);
      // console.log("Loaded company options:", formattedCompanies);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setCompanyOptions([]);
    }
  };

  const fetchTalents = async () => {
    try {
      setIsLoading(true);
      // console.log("Fetching all talents at once");
      
      // Use a large limit to get all talents in one request
      const response = await talentService.getAllTalents(1, 1000);
      // console.log("Talents response:", response);
      
      let talentsData = [];
      
      // Handle different possible response formats
      if (response.data && Array.isArray(response.data.talents)) {
        talentsData = response.data.talents;
      } else if (Array.isArray(response.data)) {
        talentsData = response.data;
      } else if (response.talents && Array.isArray(response.talents)) {
        talentsData = response.talents;
      }
      
      // console.log(`Fetched ${talentsData.length} talents`);
      
      // For each talent, fetch company relationships from the new endpoint
      const enhancedTalentsData = await Promise.all(talentsData.map(async (talent) => {
        try {
          // Try to get company data from the new endpoint
          const companyResponse = await talentService.getTalentCompanies(talent.id);
          
          // Check for nested data structure
          if (companyResponse && companyResponse.data) {
            const companyData = companyResponse.data;
            
            // Format locations properly - extract only short codes
            let formattedLocations = [];
            if (talent.locations) {
              if (Array.isArray(talent.locations)) {
                formattedLocations = talent.locations.map(loc => {
                  if (typeof loc === 'string') {
                    // If it's already a short code (like "HH", "BER"), keep it
                    return loc;
                  } else if (typeof loc === 'object' && loc.short) {
                    // If it's an object with short property, extract short code
                    return loc.short;
                  }
                  // Fallback to the original value
                  return loc;
                }).filter(Boolean); // Remove any null/undefined values
              } else if (typeof talent.locations === 'string') {
                formattedLocations = [talent.locations];
              }
            }
            
            // Merge the company data with the talent data
            const enhancedTalent = {
              id: talent.id || talent._id || `talent-${Math.random().toString(36).substr(2, 9)}`,
              Name: talent.Name || talent.name || 'Unnamed Talent',
              Funktion: talent.Funktion || talent.function || talent.funktion || '',
              Bereich: talent.Bereich || talent.bereich || talent.set || talent.area || '',
              E_Mail: talent.E_Mail || talent.email || '',
              Telefon: talent.Telefon || talent.phone || '',
              Tagessatz: talent.Tagessatz || talent.rate || '',
              Notiz_Setcast: talent.Notiz_Setcast || talent.notes || '',
              locations: formattedLocations,
              goodPerformanceCompany: companyData.goodPerformanceCompany || [],
              badPerformanceCompany: companyData.badPerformanceCompany || [],
              tryOutCompany: companyData.tryOutCompany || []
            };
            return enhancedTalent;
          }
          
          // If endpoint fails, return original talent data with formatted locations
          const formattedLocations = (talent.locations || []).map(loc => {
            if (typeof loc === 'string') {
              return loc;
            } else if (typeof loc === 'object' && loc.short) {
              return loc.short;
            }
            return loc;
          }).filter(Boolean);
          
          return {
            id: talent.id || talent._id || `talent-${Math.random().toString(36).substr(2, 9)}`,
            Name: talent.Name || talent.name || 'Unnamed Talent',
            Funktion: talent.Funktion || talent.function || talent.funktion || '',
            Bereich: talent.Bereich || talent.bereich || talent.set || talent.area || '',
            E_Mail: talent.E_Mail || talent.email || '',
            Telefon: talent.Telefon || talent.phone || '',
            Tagessatz: talent.Tagessatz || talent.rate || '',
            Notiz_Setcast: talent.Notiz_Setcast || talent.notes || '',
            locations: formattedLocations,
            goodPerformanceCompany: [],
            badPerformanceCompany: [],
            tryOutCompany: []
          };
        } catch (error) {
          // Just return the original talent data if there's an error
          // console.warn(`Could not fetch company data for talent ${talent.id}:`, error);
          const formattedLocations = (talent.locations || []).map(loc => {
            if (typeof loc === 'string') {
              return loc;
            } else if (typeof loc === 'object' && loc.short) {
              return loc.short;
            }
            return loc;
          }).filter(Boolean);
          
          return {
            id: talent.id || talent._id || `talent-${Math.random().toString(36).substr(2, 9)}`,
            Name: talent.Name || talent.name || 'Unnamed Talent',
            Funktion: talent.Funktion || talent.function || talent.funktion || '',
            Bereich: talent.Bereich || talent.bereich || talent.set || talent.area || '',
            E_Mail: talent.E_Mail || talent.email || '',
            Telefon: talent.Telefon || talent.phone || '',
            Tagessatz: talent.Tagessatz || talent.rate || '',
            Notiz_Setcast: talent.Notiz_Setcast || talent.notes || '',
            locations: formattedLocations,
            goodPerformanceCompany: [],
            badPerformanceCompany: [],
            tryOutCompany: []
          };
        }
      }));
      
      // console.log("Enhanced talents with company data:", enhancedTalentsData);
      
      // Sort talents alphabetically by name
      const sortedTalents = enhancedTalentsData.sort((a, b) => {
        const nameA = (a.Name || a.name || '').toLowerCase();
        const nameB = (b.Name || b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
      
      setTalents(sortedTalents);
      setHasMore(false);
    } catch (error) {
      console.error("Error in fetchTalents:", error);
      setTalents([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const startDate = moment(dateRange.start).format('YYYY-MM-DD');
        const endDate = moment(dateRange.end).format('YYYY-MM-DD');
        
        const response = await bookingService.getAllBookings(1, 200, startDate, endDate, null, 'deleted');
        
        // Handle different possible response formats
        let bookingsData = [];
        if (response?.data && Array.isArray(response.data.bookings)) {
          bookingsData = response.data.bookings;
        } else if (Array.isArray(response.data)) {
          bookingsData = response.data;
        } else if (Array.isArray(response.bookings)) {
          bookingsData = response.bookings;
        }
        
        setBookings(bookingsData);
      } catch (error) {
        console.error("Error fetching bookings:", error);
        setBookings([]);
      }
    };

    fetchBookings();
  }, [dateRange.start, dateRange.end]);

  const handleClientSelectChange = async (selectedOption) => {
    setSelectedClient(selectedOption);
    if (selectedOption == null) {
      setSelectedTalents([]);
    } else {
      try {
        const startDate = moment(dateRange.start).format('YYYY-MM-DD');
        const endDate = moment(dateRange.end).format('YYYY-MM-DD');
        
        // Get all bookings and filter by client
        const response = await bookingService.getAllBookings(1, 1000, startDate, endDate);
        
        // Handle different possible response formats
        let bookingsData = [];
        if (response?.data && Array.isArray(response.data.bookings)) {
          bookingsData = response.data.bookings;
        } else if (Array.isArray(response.data)) {
          bookingsData = response.data;
        } else if (Array.isArray(response.bookings)) {
          bookingsData = response.bookings;
        }
        
        // console.log('🔍 Client filter - Total bookings:', bookingsData.length);
        // console.log('🔍 Client filter - Selected client:', selectedOption.value);
        
        // Filter by client - use helper function for consistent matching
        const clientBookings = bookingsData.filter(booking => {
          const bookingClientName = getClientName(booking);
          const matches = bookingClientName === selectedOption.value;
          if (matches) {
            // console.log('✅ Matching booking found:', {
              // bookingId: booking.id,
              // client: bookingClientName,
              // talentId: booking.talent_id || booking.sql_talent_id
            // });
          }
          return matches;
        });
        
        // console.log('🔍 Client filter - Matching bookings:', clientBookings.length);
        
        if (clientBookings.length === 0) {
          setShowNoBookingsPopup(true);
          setSelectedClient(null);
          setTimeout(() => setShowNoBookingsPopup(false), 3000);
          return;
        }

        // Extract talent IDs - handle both talent_id and sql_talent_id
        const talentIds = [...new Set(
          clientBookings
            .map(booking => booking.talent_id || booking.sql_talent_id)
            .filter(Boolean) // Remove null/undefined
            .map(id => String(id)) // Convert to string for consistent comparison
        )];
        
        // console.log('🔍 Client filter - Unique talent IDs:', talentIds);
        
        // Filter talents - convert talent.id to string for comparison
        const filteredTalents = talents.filter(talent => 
          talentIds.includes(String(talent.id))
        );
        
        // console.log('🔍 Client filter - Filtered talents:', filteredTalents.length);
        
        const optionsForSelect = filteredTalents
          .map(talent => ({
            value: talent.id,
            label: talent.name || talent.Name
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        setSelectedTalents(optionsForSelect);
      } catch (error) {
        console.error("Error fetching bookings for client:", error);
        setSelectedTalents([]);
      }
    }
  };

  const handleSttatus = async (selectedOption) => {
    if (selectedOption == null) {
      setSelectedTalents([]);
      setSelectedStatus('');
    } else {
      setSelectedStatus(selectedOption);
      try {
        const startDate = moment(dateRange.start).format('YYYY-MM-DD');
        const endDate = moment(dateRange.end).format('YYYY-MM-DD');
        
        // Get bookings with the specific status
        const response = await bookingService.getAllBookings(1, 1000, startDate, endDate, selectedOption.value);
        
        // Handle different possible response formats
        let statusBookings = [];
        if (response?.data && Array.isArray(response.data.bookings)) {
          statusBookings = response.data.bookings;
        } else if (Array.isArray(response.data)) {
          statusBookings = response.data;
        } else if (Array.isArray(response.bookings)) {
          statusBookings = response.bookings;
        }
        
        // Extract talent IDs - handle both talent_id and sql_talent_id
        const talentIds = [...new Set(
          statusBookings
            .map(booking => booking.talent_id || booking.sql_talent_id)
            .filter(Boolean) // Remove null/undefined
            .map(id => String(id)) // Convert to string for consistent comparison
        )];
        
        // Filter talents - convert talent.id to string for comparison
        const filteredTalents = talents.filter(talent => 
          talentIds.includes(String(talent.id))
        );
        
        const optionsForSelect = filteredTalents
          .map(talent => ({
            value: talent.id,
            label: talent.name || talent.Name
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        setSelectedTalents(optionsForSelect);
      } catch (error) {
        console.error("Error fetching bookings by status:", error);
        setSelectedTalents([]);
      }
    }
  };

  const handleGoodCompaniesChange = (selectedOptions) => {
    setFilterGoodCompanies(selectedOptions || []);
  };

  const handleBadCompaniesChange = (selectedOptions) => {
    setFilterBadCompanies(selectedOptions || []);
  };

  const handleTryoutCompaniesChange = (selectedOptions) => {
    setFilterTryoutCompanies(selectedOptions || []);
  };

  const filteredTalents = useMemo(() => {
    const filtered = talents.filter(talent => {
      // Search filter - check name and function
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const talentName = (talent.Name || talent.name || '').toLowerCase();
        const talentFunction = (talent.Funktion || talent.function || talent.funktion || '').toLowerCase();
        const talentArea = (talent.Bereich || talent.bereich || talent.set || talent.area || '').toLowerCase();
        
        const matchesSearch = talentName.includes(query) || 
                            talentFunction.includes(query) || 
                            talentArea.includes(query);
        
        if (!matchesSearch) {
          return false;
        }
      }

      if (selectedTalents.length > 0 && 
          !selectedTalents.some(selected => selected.value === talent.id)) {
        return false;
      }

      const talentBereich = talent.bereich || talent.Bereich;
      const talentFunktion = talent.funktion || talent.Funktion;

      if (selectedSet && talentBereich !== selectedSet.value) {
        return false;
      }

      if (selectedFunc?.length > 0 && 
          !selectedFunc.some(func => func.value === talentFunktion)) {
        return false;
      }

      if (selectedLocations?.length > 0) {
        if (!talent.locations || !Array.isArray(talent.locations)) {
          return false;
        }
        // Extract selected location short codes
        const selectedLocationShorts = selectedLocations.map(loc => loc.value);
        // Check if any of the talent's locations (short codes) match the selected ones
        const hasMatchingLocation = selectedLocationShorts.some(selectedShort =>
          talent.locations.includes(selectedShort)
        );
        
        // Debug logging
        // console.log(`Filtering talent ${talent.Name}:`, {
          // talentLocations: talent.locations,
          // selectedLocationShorts,
          // hasMatchingLocation
        // });
        
        if (!hasMatchingLocation) {
          return false;
        }
      }

      if (selectedClient) {
        const hasMatchingBooking = bookings.some(booking => {
          // Check talent ID - handle both talent_id and sql_talent_id
          const bookingTalentId = booking.talent_id || booking.sql_talent_id;
          const talentMatches = String(bookingTalentId) === String(talent.id);
          
          // Check client - use helper function for consistent matching
          const bookingClientName = getClientName(booking);
          const clientMatches = bookingClientName === selectedClient.value;
          
          return talentMatches && clientMatches;
        });
        
        if (!hasMatchingBooking) {
          return false;
        }
      }

      if (selectedStatus) {
        const hasMatchingBooking = bookings.some(booking => {
          // Check talent ID - handle both talent_id and sql_talent_id
          const bookingTalentId = booking.talent_id || booking.sql_talent_id;
          const talentMatches = String(bookingTalentId) === String(talent.id);
          
          // Check status
          const statusMatches = booking.status === selectedStatus.value;
          
          return talentMatches && statusMatches;
        });
        
        if (!hasMatchingBooking) {
          return false;
        }
      }

      // Filter by good performance companies - check talent's goodPerformanceCompany array
      if (filterGoodCompanies.length > 0) {
        const hasGoodPerformance = filterGoodCompanies.some(selectedCompany => {
          if (!Array.isArray(talent.goodPerformanceCompany)) return false;
          return talent.goodPerformanceCompany.some(company => {
            // Handle both string and object formats
            if (typeof company === 'string') {
              return company === selectedCompany.label;
            } else if (company && typeof company === 'object') {
              return company.value === selectedCompany.value || company.label === selectedCompany.label;
            }
            return false;
          });
        });
        if (!hasGoodPerformance) return false;
      }

      // Filter by bad performance companies - check talent's badPerformanceCompany array
      if (filterBadCompanies.length > 0) {
        const hasBadPerformance = filterBadCompanies.some(selectedCompany => {
          if (!Array.isArray(talent.badPerformanceCompany)) return false;
          return talent.badPerformanceCompany.some(company => {
            // Handle both string and object formats
            if (typeof company === 'string') {
              return company === selectedCompany.label;
            } else if (company && typeof company === 'object') {
              return company.value === selectedCompany.value || company.label === selectedCompany.label;
            }
            return false;
          });
        });
        if (!hasBadPerformance) return false;
      }

      // Filter by tryout companies - check talent's tryOutCompany array
      if (filterTryoutCompanies.length > 0) {
        const hasTryOut = filterTryoutCompanies.some(selectedCompany => {
          if (!Array.isArray(talent.tryOutCompany)) return false;
          return talent.tryOutCompany.some(company => {
            // Handle both string and object formats
            if (typeof company === 'string') {
              return company === selectedCompany.label;
            } else if (company && typeof company === 'object') {
              return company.value === selectedCompany.value || company.label === selectedCompany.label;
            }
            return false;
          });
        });
        if (!hasTryOut) return false;
      }

      return true;
    });

    // Sort filtered talents alphabetically by name
    return filtered.sort((a, b) => {
      const nameA = (a.Name || a.name || '').toLowerCase();
      const nameB = (b.Name || b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [
    talents,
    selectedTalents,
    selectedSet,
    selectedFunc,
    selectedLocations,
    selectedClient,
    selectedStatus,
    bookings,
    filterGoodCompanies,
    filterBadCompanies,
    filterTryoutCompanies,
    searchQuery
  ]);

  // Batch-fetch schedule data for all visible talents when date range or talent list changes
  useEffect(() => {
    if (!filteredTalents.length) return;
    const talentIds = filteredTalents.map(t => t.id).filter(Boolean);
    const start = moment(dateRange.start).format('YYYY-MM-DD');
    const end = moment(dateRange.end).format('YYYY-MM-DD');
    talentScheduleService.getTalentScheduleRangeBatch(talentIds, start, end)
      .then(response => {
        if (response?.data?.data) {
          setScheduleDataMap(prev => ({ ...prev, ...response.data.data }));
        }
      })
      .catch(err => console.error('Batch schedule fetch failed:', err));
  }, [filteredTalents, dateRange, setScheduleDataMap]);

  const talentOptions = useMemo(() =>
    talents.map(talent => {
      const name = talent.name || talent.Name || '';
      const funktion = talent.funktion || talent.Funktion || talent.function || '';
      const bereich = talent.bereich || talent.Bereich || talent.set || talent.area || '';
      return {
        value: talent.id,
        label: `${name} - ${funktion} - ${bereich}`
      };
    }).sort((a, b) => a.label.localeCompare(b.label)),
    [talents]
  );

  const talentsetOptions = useMemo(() => 
    Array.from(new Set(talents.map(talent => talent.bereich || talent.Bereich || talent.set || talent.area)))
      .filter(Boolean)
      .map(bereich => ({
        value: bereich,
        label: bereich
      })),
    [talents]
  );

  const talentFuncOptions = useMemo(() => 
    Array.from(new Set(talents.map(talent => talent.funktion || talent.Funktion || talent.function)))
      .filter(Boolean)
      .map(funktion => ({
        value: funktion,
        label: funktion
      })),
    [talents]
  );



  const clientOptions = useMemo(() => {
    // Use helper function to extract client names consistently
    const clientNames = bookings
      .map(booking => getClientName(booking))
      .filter(Boolean); // Remove null/undefined
    
    return Array.from(new Set(clientNames))
      .map(name => ({
        value: name,
        label: name
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [bookings]);

  const loadMoreTalents = async () => {
    if (!hasMore || isLoading) return;
    
    try {
      setIsLoading(true);
      const nextPage = currentPage + 1;
      // console.log(`Loading more talents (page ${nextPage})`);
      
      const response = await talentService.getAllTalents(nextPage, 50);
      // console.log("Additional talents response:", response);
      
      let additionalTalents = [];
      
      // Handle different possible response formats
      if (response.data && Array.isArray(response.data.talents)) {
        additionalTalents = response.data.talents;
      } else if (Array.isArray(response.data)) {
        additionalTalents = response.data;
      } else if (response.talents && Array.isArray(response.talents)) {
        additionalTalents = response.talents;
      }
      
      // console.log(`Fetched ${additionalTalents.length} additional talents`);
      
      if (additionalTalents.length === 0) {
        setHasMore(false);
        return;
      }
      
      // Format the additional talents
      const formattedAdditionalTalents = additionalTalents.map(talent => {
        const talentId = talent.id || talent._id || `talent-${Math.random().toString(36).substr(2, 9)}`;
        
        // Format locations properly - extract only short codes
        let formattedLocations = [];
        if (talent.locations) {
          if (Array.isArray(talent.locations)) {
            formattedLocations = talent.locations.map(loc => {
              if (typeof loc === 'string') {
                // If it's already a short code (like "HH", "BER"), keep it
                return loc;
              } else if (typeof loc === 'object' && loc.short) {
                // If it's an object with short property, extract short code
                return loc.short;
              }
              // Fallback to the original value
              return loc;
            }).filter(Boolean); // Remove any null/undefined values
          } else if (typeof talent.locations === 'string') {
            formattedLocations = [talent.locations];
          }
        }
        
        return {
          id: talentId,
          Name: talent.Name || talent.name || 'Unnamed Talent',
          Funktion: talent.Funktion || talent.function || talent.funktion || '',
          Bereich: talent.Bereich || talent.bereich || talent.set || talent.area || '',
          E_Mail: talent.E_Mail || talent.email || '',
          Telefon: talent.Telefon || talent.phone || '',
          Tagessatz: talent.Tagessatz || talent.rate || '',
          Notiz_Setcast: talent.Notiz_Setcast || talent.notes || '',
          locations: formattedLocations
        };
      });
      
      // Merge with existing talents
      setTalents(prevTalents => [...prevTalents, ...formattedAdditionalTalents]);
      setCurrentPage(nextPage);
      setHasMore(formattedAdditionalTalents.length >= 50); // If we got 50 items, there might be more
    } catch (error) {
      console.error("Error loading more talents:", error);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    // Note: Filtering is now done client-side via useMemo, no API call needed
  };

  // Load talent groups from API on mount
  useEffect(() => {
    const fetchTalentGroups = async () => {
      try {
        const response = await talentGroupService.getAllGroups(1, 100);
        if (response.data?.success && response.data?.data?.groups) {
          // Transform API response to match our format
          const groups = response.data.data.groups.map(group => ({
            id: group.id.toString(),
            name: group.name,
            talentIds: group.talent_ids || [],
            createdAt: group.created_at
          }));
          setTalentGroups(groups);
        }
      } catch (error) {
        console.error('Error loading talent groups from API:', error);
        // If API fails, try to load from localStorage as fallback
        const savedGroups = localStorage.getItem('talentGroups');
        if (savedGroups) {
          try {
            const parsedGroups = JSON.parse(savedGroups);
            setTalentGroups(parsedGroups);
          } catch (parseError) {
            console.error('Error loading talent groups from localStorage:', parseError);
          }
        }
      }
    };

    fetchTalentGroups();
  }, []);

  // Talent Groups functions
  const openGroupModal = (group = null) => {
    if (group) {
      setEditingGroup(group);
      setNewGroupName(group.name);
      setNewGroupTalents(group.talentIds.map(id => {
        const talent = talents.find(t => t.id === id);
        return talent ? { value: talent.id, label: `${talent.Name || talent.name} - ${talent.Funktion || talent.funktion || ''} - ${talent.Bereich || talent.bereich || ''}` } : null;
      }).filter(Boolean));
    } else {
      setEditingGroup(null);
      setNewGroupName('');
      setNewGroupTalents([]);
    }
    setIsGroupModalOpen(true);
  };

  const closeGroupModal = () => {
    setIsGroupModalOpen(false);
    setEditingGroup(null);
    setNewGroupName('');
    setNewGroupTalents([]);
  };

  const saveGroup = async () => {
    if (!newGroupName.trim()) {
      alert('Please enter a group name');
      return;
    }

    if (newGroupTalents.length === 0) {
      alert('Please select at least one talent for the group');
      return;
    }

    const talentIds = newGroupTalents.map(t => t.value);

    try {
      if (editingGroup) {
        // Update existing group via API
        const response = await talentGroupService.updateGroup(editingGroup.id, {
          name: newGroupName.trim(),
          talent_ids: talentIds
        });
        
        if (response.data?.success) {
          // Update local state
          setTalentGroups(prev => prev.map(g => 
            g.id === editingGroup.id 
              ? { ...g, name: newGroupName.trim(), talentIds }
              : g
          ));
        } else {
          throw new Error(response.data?.message || 'Failed to update group');
        }
      } else {
        // Create new group via API
        const response = await talentGroupService.createGroup({
          name: newGroupName.trim(),
          talent_ids: talentIds
        });
        
        if (response.data?.success && response.data?.data) {
          const newGroup = {
            id: response.data.data.id.toString(),
            name: response.data.data.name,
            talentIds: response.data.data.talent_ids || [],
            createdAt: response.data.data.created_at
          };
          setTalentGroups(prev => [...prev, newGroup]);
        } else {
          throw new Error(response.data?.message || 'Failed to create group');
        }
      }
      
      closeGroupModal();
    } catch (error) {
      console.error('Error saving talent group:', error);
      alert('Error saving group: ' + (error.response?.data?.message || error.message));
    }
  };

  const deleteGroup = async (groupId) => {
    if (window.confirm('Are you sure you want to delete this group?')) {
      try {
        const response = await talentGroupService.deleteGroup(groupId);
        
        if (response.data?.success) {
          // Update local state
          setTalentGroups(prev => prev.filter(g => g.id !== groupId));
          // Remove deleted group from selected groups if it was selected
          setSelectedGroups(prev => prev.filter(g => g.value !== groupId));
          // If no groups are selected anymore, clear talents
          const remainingSelectedGroups = selectedGroups.filter(g => g.value !== groupId);
          if (remainingSelectedGroups.length === 0) {
            setSelectedTalents([]);
          } else {
            // Recalculate selected talents from remaining groups
            handleGroupSelect(remainingSelectedGroups);
          }
        } else {
          throw new Error(response.data?.message || 'Failed to delete group');
        }
      } catch (error) {
        console.error('Error deleting talent group:', error);
        alert('Error deleting group: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const handleGroupSelect = (selectedOptions) => {
    setSelectedGroups(selectedOptions || []);
    if (selectedOptions && selectedOptions.length > 0) {
      // Collect all unique talent IDs from all selected groups
      const allTalentIds = new Set();
      selectedOptions.forEach(selectedOption => {
        const group = talentGroups.find(g => g.id === selectedOption.value);
        if (group && group.talentIds) {
          group.talentIds.forEach(id => allTalentIds.add(id));
        }
      });
      
      // Map all unique talent IDs to talent options
      const groupTalents = Array.from(allTalentIds)
        .map(id => {
          const talent = talents.find(t => t.id === id);
          if (talent) {
            const name = talent.Name || talent.name || '';
            const funktion = talent.Funktion || talent.funktion || talent.function || '';
            const bereich = talent.Bereich || talent.bereich || talent.set || talent.area || '';
            return {
              value: talent.id,
              label: `${name} - ${funktion} - ${bereich}`
            };
          }
          return null;
        })
        .filter(Boolean);
      
      setSelectedTalents(groupTalents);
    } else {
      setSelectedTalents([]);
    }
  };

  const groupOptions = talentGroups.map(group => ({
    value: group.id,
    label: `${group.name} (${group.talentIds.length} talents)`
  }));

  useEffect(() => {
    fetchLocations();
    fetchCompanies();
    fetchTalents();
  }, []);

  return (
    <div className="content-wrapper page">
      <div className="input-container" style={{display:'inline'}}>
        {/* First row: Search + 4 filters */}
        <div style={{display:'flex', marginBottom: '10px', gap: '5px',alignItems: 'center'}}>
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearch}
            placeholder="Search..."
            style={{
              minWidth: '250px',
              padding: '12px 18px',
              border: '1px solid #000',
              fontSize: '1.2rem'
            }}
          />
          
          <Select
            isMulti
            isClearable
            value={selectedGroups}
            options={groupOptions}
            onChange={handleGroupSelect}
            className="multi-select"
            classNamePrefix="select"
            placeholder="Talent Group"
            styles={localCustomStyles}
          />
          
          <Select
            isMulti
            options={talentOptions}
            value={selectedTalents}
            onChange={(newValue) => {
              setSelectedTalents(newValue);
              // Reset expanded state when selection changes
              setIsTalentListExpanded(false);
              // Clear group selection if talents are manually changed
              if (selectedGroups.length > 0) {
                setSelectedGroups([]);
              }
            }}
            className="multi-select"
            classNamePrefix="select"
            placeholder="Talents"
            isClearable
            styles={localCustomStyles}
            filterOption={(option, input) => {
              if (!input) return true;
              return option.label.toLowerCase().includes(input.toLowerCase());
            }}
            isLoading={isLoading}
            components={{
              ValueContainer: CustomValueContainer
            }}
          />

          <Select
            isClearable
            value={selectedClient}
            options={clientOptions}
            className="multi-select"
            classNamePrefix="select"
            onChange={handleClientSelectChange}
            placeholder="Client"
            styles={localCustomStyles}
          />

          <Select
            isClearable
            value={selectedStatus}
            options={bookingStatusOptions}
            className="multi-select"
            classNamePrefix="select"
            onChange={handleSttatus}
            placeholder="Status"
            styles={localCustomStyles}
          />

          <Select
            isClearable
            isMulti
            value={selectedFunc}
            options={talentFuncOptions}
            className="multi-select"
            classNamePrefix="select"
            onChange={setSelectedFunc}
            placeholder="Function"
            styles={localCustomStyles}
          />
        </div>

        {/* Second row: 5 filters */}
        <div style={{display:'flex', marginBottom: '10px', gap: '5px'}}>
          <Select
            isClearable
            value={selectedLocations}
            isMulti
            options={locationOptions}
            className="multi-select"
            classNamePrefix="select"
            onChange={setSelectedLocations}
            placeholder="Locations"
            styles={localCustomStyles}
          />

          <Select
            isClearable
            value={selectedSet}
            options={talentsetOptions}
            className="multi-select"
            classNamePrefix="select"
            onChange={setSelectedSet}
            placeholder="Set"
            styles={localCustomStyles}
          />

          <Select
            isMulti
            value={filterGoodCompanies}
            onChange={handleGoodCompaniesChange}
            options={companyOptions}
            className="multi-select"
            classNamePrefix="select"
            placeholder="Good Performance"
            isClearable
            styles={localCustomStyles}
          />

          <Select
            isMulti
            value={filterBadCompanies}
            onChange={handleBadCompaniesChange}
            options={companyOptions}
            className="multi-select"
            classNamePrefix="select"
            placeholder="Bad Performance"
            isClearable
            styles={localCustomStyles}
          />

          <Select
            isMulti
            value={filterTryoutCompanies}
            onChange={handleTryoutCompaniesChange}
            options={companyOptions}
            className="multi-select"
            classNamePrefix="select"
            placeholder="Try Out"
            isClearable
            styles={localCustomStyles}
          />
        </div>
      </div>

      <DateNavigator />
      
      <div style={{ marginTop: "30px", height: "calc(100vh - 250px)" }}>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <ClipLoader color="#000000" size={30} loading={true} />
            <p style={{ marginTop: '15px', fontSize: '16px' }}>
              Loading all talents... This may take a moment.
            </p>
          </div>
        ) : filteredTalents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            No talents found matching the current filters.
          </div>
        ) : (
          <div>

            <List
              height={window.innerHeight - 300}
              itemCount={filteredTalents.length}
              itemSize={150}
              width="100%"
              style={{ overflowX: 'hidden' }}
              overscanCount={5}
              itemData={filteredTalents}
            >
              {({ index, style, data }) => (
                <div style={style} className="talent-list-item">
                  <Talent talent={data[index]} />
                </div>
              )}
            </List>
          </div>
        )}
      </div>

      {showNoBookingsPopup && (
        <div 
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: '#fff',
            padding: '50px',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            zIndex: 1000,
            border: '1px solid #ccc'
          }}
        >
          <p style={{ margin: 0, fontSize: '20px', fontWeight: 'bold' }}>
            No bookings for the selected client were found on this week
          </p>
        </div>
      )}

      {/* Talent Group Management Modal */}
      <Modal
        isOpen={isGroupModalOpen}
        onRequestClose={closeGroupModal}
        contentLabel="Manage Talent Groups"
        style={{
          overlay: {
            zIndex: 9999,
          },
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
            minWidth: "600px",
            maxWidth: "800px",
            maxHeight: "80vh",
            overflow: "auto"
          },
        }}
      >
        <div style={{ padding: "20px" }}>
          <h2>{editingGroup ? 'Edit Talent Group' : 'Create Talent Group'}</h2>
          
          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
              Group Name:
            </label>
            <input
              type="text"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="Enter group name"
              style={{
                width: "100%",
                padding: "8px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px"
              }}
            />
          </div>

          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", marginBottom: "5px", fontWeight: "bold" }}>
              Select Talents:
            </label>
            <Select
              isMulti
              options={talentOptions}
              value={newGroupTalents}
              onChange={setNewGroupTalents}
              className="multi-select"
              classNamePrefix="select"
              placeholder="Select talents for this group"
              styles={localCustomStyles}
              filterOption={(option, input) => {
                if (!input) return true;
                return option.label.toLowerCase().includes(input.toLowerCase());
              }}
            />
          </div>

          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
            <button className="button" onClick={saveGroup}>
              {editingGroup ? 'Update Group' : 'Create Group'}
            </button>
            <button className="button" onClick={closeGroupModal}>
              Cancel
            </button>
          </div>

          <div style={{ marginTop: "30px", borderTop: "1px solid #ccc", paddingTop: "20px" }}>
            <h3>Existing Groups ({talentGroups.length})</h3>
            {talentGroups.length === 0 ? (
              <p>No groups created yet.</p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "8px", maxHeight: "200px", overflowY: "auto" }}>
                {talentGroups.map(group => (
                  <div
                    key={group.id}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      padding: "8px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      backgroundColor: "#f9f9f9",
                      fontSize: "12px"
                    }}
                  >
                    <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
                      {group.name}
                    </div>
                    <div style={{ color: "#666", fontSize: "11px", marginBottom: "6px" }}>
                      {group.talentIds.length} talent{group.talentIds.length !== 1 ? 's' : ''}
                    </div>
                    <div style={{ display: "flex", gap: "4px" }}>
                      <button
                        onClick={() => openGroupModal(group)}
                        style={{
                          padding: "3px 8px",
                          border: "1px solid #000",
                          backgroundColor: "#fff",
                          cursor: "pointer",
                          fontSize: "11px",
                          flex: 1
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => deleteGroup(group.id)}
                        style={{
                          padding: "3px 8px",
                          border: "1px solid #ff0000",
                          backgroundColor: "#fff",
                          color: "#ff0000",
                          cursor: "pointer",
                          fontSize: "11px",
                          flex: 1
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

const TalentListCalendar = () => {
  return (
    <CalendarProvider>
      <TalentListCalendarmain />
    </CalendarProvider>
  );
};

export default TalentListCalendar;
